import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { dashboardSettingsService } from '../services/apiClient';

export type AppLanguage = 'en' | 'af';
export type AppCurrency = 'USD' | 'ZAR';

interface LocalizationContextType {
  language: AppLanguage;
  currency: AppCurrency;
  locale: string;
  setLanguage: (language: AppLanguage) => void;
  setCurrency: (currency: AppCurrency) => void;
  formatCurrency: (amount: number) => string;
  formatDate: (value: string | Date, options?: Intl.DateTimeFormatOptions) => string;
  formatMonthYear: (value: string | Date) => string;
  t: (key: string) => string;
}

const languageLocales: Record<AppLanguage, string> = {
  en: 'en-US',
  af: 'af-ZA',
};

const translations: Record<AppLanguage, Record<string, string>> = {
  en: {
    appTitle: 'Savannah Accounts',
    dashboard: 'Dashboard',
    accountHolders: 'Account Holders',
    accountManagement: 'Account Management',
    accountOverview: 'Transaction Summary',
    accountOverviewTitle: 'Account Overview',
    farmWork: 'Expenses',
    payments: 'Payments',
    statements: 'Statements',
    settings: 'Settings',
    more: 'More',
    userManagement: 'User Management',
    profile: 'Profile',
    dashboardSettings: 'Admin System Settings',
    language: 'Language',
    currency: 'Currency',
    logout: 'Logout',
    loadingApplication: 'Loading application...',
    loading: 'Loading...',
    close: 'Close',
    cancel: 'Cancel',
    draftSaved: 'Draft saved',
    save: 'Save',
    update: 'Update',
    edit: 'Edit',
    delete: 'Delete',
    remove: 'Remove',
    actions: 'Actions',
    rows: 'Rows',
    active: 'Active',
    inactive: 'Inactive',
    yes: 'Yes',
    no: 'No',
    send: 'Send',
    selectAll: 'Select All',

    login: 'Login',
    registering: 'Registering...',
    loggingIn: 'Logging in...',
    register: 'Register',
    username: 'Username',
    password: 'Password',
    email: 'Email',
    firstName: 'First Name',
    lastName: 'Last Name',
    confirmPassword: 'Confirm Password',
    loginFailed: 'Login failed',
    errorDuringLogin: 'An error occurred during login',
    registerFailed: 'Registration failed',
    errorDuringRegister: 'An error occurred during registration',
    passwordsDoNotMatch: 'Passwords do not match',
    passwordMinChars: 'Password must be at least 6 characters long',
    defaultAdminUsernameLabel: 'Default admin username',
    defaultAdminPasswordLabel: 'Default admin password',
    dontHaveAccount: "Don't have an account?",
    contactAdminOr: 'Contact your administrator or',
    contactAdminToRegister: 'To create an account, please contact your administrator.',
    createNewAccount: 'Create a new account',
    createAccountSubtitle: 'Create an account to access your trust account management system',
    alreadyHaveAccount: 'Already have an account?',
    loginHere: 'Login here',
    chooseUsernamePlaceholder: 'Choose a username',
    enterEmailPlaceholder: 'Enter your email',
    enterFirstNamePlaceholder: 'Enter your first name',
    enterLastNamePlaceholder: 'Enter your last name',
    strongPasswordPlaceholder: 'Enter a strong password',
    confirmPasswordPlaceholder: 'Confirm your password',
    atLeast6Chars: 'At least 6 characters',
    leaveBlankKeepCurrent: 'Leave blank to keep current',
    maintenanceTitle: 'Under Maintenance',
    maintenanceMessage: 'We are currently performing scheduled maintenance.',
    maintenanceExpected: 'We expect to be back online shortly. Thank you for your patience.',
    maintenanceContact: 'If you have any urgent questions, please contact your administrator.',
    userRole: 'User',
    adminRole: 'Admin',
    english: 'English',
    afrikaans: 'Afrikaans',
    currencyUsd: 'USD',
    currencyZar: 'ZAR (R)',

    accountHoldersTitle: 'Account Holders',
    manageAccountHoldersSubtitle: 'Manage your account holder records and communication in one place.',
    addNewAccountHolder: 'Add New Account Holder',
    noAccountHoldersFound: 'No account holders found',
    loadingAccountHolders: 'Loading account holders...',
    addresses: 'Addresses',
    linkedAddresses: 'Linked Addresses',
    linkedAddressesWithFees: 'Address Lines (Monthly Fee)',
    monthlyFeeHistory: 'Monthly Fee History',
    effectiveFromMonth: 'Effective From Month',
    changedAtLabel: 'Changed At',
    addAddress: 'Add Address',
    monthlyFee: 'Monthly Fee',
    addressLineCannotBeBlank: 'Address line cannot be blank',
    duplicateAddressesNotAllowed: 'Duplicate addresses are not allowed',
    duplicateEmailsNotAllowed: 'Duplicate email addresses are not allowed',
    addressGroups: 'address groups',
    noAddressesAssigned: 'Unassigned Address',
    customEmailSent: 'Your email has been sent to the selected account holder(s).',
    composeSendEmail: 'Compose & Send Email',
    sendingToLabel: 'Sending to:',
    noRecipientsSelected: 'No recipients selected',
    addRecipientPlaceholder: 'Add recipient...',
    subjectRequired: 'Subject *',
    emailSubjectPlaceholder: 'Enter a clear, concise subject line...',
    messageRequired: 'Message *',
    emailGreeting: 'Hello',
    emailFooterNoReply: 'This email was sent via Savannah Accounts. Please do not reply directly to this message.',

    paymentsTitle: 'Payments',
    addNewPayment: 'Add New Payment',
    addNewPayments: 'Add New Payments',
    loadingPayments: 'Loading payments...',
    noPaymentsFound: 'No payments found',
    filters: 'Filters',
    month: 'Month',
    reset: 'Reset',
    amount: 'Amount',
    description: 'Description',
    paymentDate: 'Payment Date',
    totalAmount: 'Total Amount',
    transactions: 'Transactions',
    accountHolder: 'Account Holder',
    allAccountHolders: 'All Account Holders',

    statementsTitle: 'Payment Statements',
    sendAllStatements: 'Send All Statements',
    sendingStatements: 'Sending Statements...',
    sendingAction: 'Sending...',
    sendEmail: 'Send Email',
        selectRecipientBeforeSending: 'Please select at least one recipient before sending.',
        enterEmailSubject: 'Please enter a subject',
        enterEmailMessage: 'Please enter a message',
        failedToSendCustomEmail: 'Failed to send custom email. Please check your connection or try again later.',
    print: 'Print',
    sending: 'Sending...',
    view: 'View',
    period: 'Period',
    statementDetails: 'Statement Details',
    detailedTransactions: 'Detailed Transactions',
    monthlyAccountFees: 'Monthly Account Fees',
    accountFeeBreakdown: 'Account Fee Breakdown',
    includesMonthlyAccountFees: 'Includes monthly account fees',
    bankDetails: 'Bank Details',
    accountName: 'Account Name',
    bankNameBranchCode: 'Bank Name - Branch Code',
    accountTypeAccountNumber: 'Account Type - Account Number',
    accountNumber: 'Account Number',
    branchCode: 'Absa Bank - Branch Code',
    paymentReference: 'Payment Reference',
    paymentReferenceDescription: 'Initials, surname and erf/plot number',
    sendSelectedEmails: 'Send Email(s)',
    confirmSendSelectedStatements: 'Send statements to the selected contributors?',
    selectAllContributors: 'Select all contributors',
    selectContributor: 'Select contributor',
    selectedContributorsCount: 'Selected contributors',
    selectContributorBeforeSending: 'Please select at least one contributor before sending.',
    selectContributorBeforePrinting: 'Please select at least one contributor before printing.',
    sendStatementsToFiltered: 'Send Filtered Statements',
    confirmSendStatementsToFiltered: 'Send statements only to the selected account holder?',
    filtered: 'Filtered',
    farmWorkMonthlyOverview: 'Expenses Monthly Overview',
    farmWorkContributionOverview: 'Expenses Contribution Overview',
    requiredContribution: 'Required Contribution',
    totalRequiredContribution: 'Total Required Contribution',
    appliedToFarmWork: 'Applied to Expenses',
    amountPaidFromStatements: 'Amount Paid (Statements)',
    statementTotalForMonth: 'Statement Total (Month)',
    netStatementMovement: 'Net Statement Movement',
    outstandingFees: 'Outstanding Fees',
    outstanding: 'Outstanding',
    overpaidAmount: 'Overpaid Amount',
    overpaidShort: 'Overpaid',
    estimatedPaid: 'Estimated Paid',
    totalCredits: 'Total Credits',
    totalDebits: 'Total Debits',
    creditsVsDebits: 'Credits / Debits',
    contributorsStatus: 'Contributors Status',
    notPaid: 'Not Paid',
    paidInFull: 'Paid in Full',
    withOutstanding: 'With Outstanding',
    partiallyPaid: 'Partially Paid',
    noContributionDue: 'No Contribution Due',
    holderHasOutstandingFees: 'This account holder has outstanding expense fees',
    costCoverageGap: 'Cost Coverage Gap',
    noFarmWorkForSelectedMonth: 'No expense items found for the selected month',
    noHolderContributionOrPaymentsForMonth: 'No account holder contributions or payments found for',
    allocationMethodLabel: 'Allocation method',
    allocationMethodOldestFirst: 'Payments are allocated to expense obligations oldest-first (by created date), then by item ID.',
    date: 'Date',
    status: 'Status',
    trustAccount: 'Trust Account',
    addTrustAccount: 'Add Account',
    addTrustAccountTitle: 'Add Account',
    accountType: 'Account Type',
    currentAccountType: 'Current',
    savingsAccountType: 'Savings',
    activeAccount: 'Active Account',
    switchAccounts: 'Switch Accounts',
    switchAccountsPrompt: 'Choose which account you want to view on this page.',
    trustBankNameInputLabel: 'Bank Name',
    trustAccountNameLabel: 'Account Name',
    trustAccountNumberInputLabel: 'Account Number',
    trustBranchCodeInputLabel: 'Branch Code',
    oneAccountPerTypeAllowed: 'Only one account per type is allowed.',
    allTrustAccountTypesConfigured: 'Current and Savings accounts already exist.',
    trustAccountAddedSuccessfully: 'Trust account added successfully',
    trustAccountUpdatedSuccessfully: 'Trust account updated successfully',
    couldNotAddTrustAccount: 'Could not add trust account',
    couldNotUpdateTrustAccount: 'Could not update trust account',
    accountTypeAlreadyExistsError: 'An account with this type already exists.',
    accountNameAlreadyExistsError: 'An account with this name already exists. Account names must be unique.',
    accountNumberAlreadyExistsError: 'An account with this number already exists. Account numbers must be unique.',
    trustAccountManagement: 'Account Management',
    trustAccountManagementSubtitle: 'Admin-only maintenance for trust account setup.',
    trustAccountManagementAdminOnly: 'Only administrators can manage trust accounts.',
    existingTrustAccounts: 'Existing Trust Accounts',
    noTrustAccountsConfigured: 'No trust accounts configured yet.',

    userManagementTitle: 'User Management',
    usersLabel: 'Users',
    addNewUser: 'Add New User',
    noUsersFound: 'No users found',
    loadingUsers: 'Loading users...',
    role: 'Role',
    created: 'Created',
    lastLogin: 'Last Login',
    never: 'Never',
    createNewUser: 'Create New User',
    editUser: 'Edit User',

    farmWorkManagement: 'Expense Management',
    farmWorkItems: 'Expense Items',
    farmWorkTypes: 'Expense Types',
    addFarmWork: 'Add Expense',
    addFarmWorks: 'Add Expenses',
    addFarmWorkType: 'Add Expense Types',
    addExpenseModalHelperTitle: 'Quick expense entry',
    addExpenseModalHelperText: 'Save the expense first, and only add contributors if needed. Contributors are optional and can be added later.',
    selectMonthYear: 'Date',
    noFarmWorkItemsForMonth: 'No expense items for selected month/year',
    noFarmWorkTypesFound: 'No expense types found',
    contributors: 'Contributors',
    contributions: 'Contributions',
    contributionsDetails: 'Contributions Details',
    noContributionsYet: 'No contributions recorded yet',
    cost: 'Cost',
    type: 'Type',
    coverage: 'Coverage',
    totalContributions: 'Total Contributions',
    totalFarmWorkCost: 'Total Expenses',

    dashboardTitle: 'Trust Account Dashboard',
    allAccounts: 'All Accounts',
    trackedAccounts: 'tracked accounts',
    monthlyComparison: 'Monthly Comparison',
    lastFourMonths: 'Last 4 Months',
    mainTrustAccount: 'Main Trust Account',
    loadingDashboard: 'Loading dashboard...',
    failedToLoadData: 'Could not load data',
    couldNotLoadData: 'Could not load data',
    failedToLoadDashboardData: 'Could not load dashboard data',
    failedToLoadUsers: 'Could not load users',
    failedToLoadAccountHolders: 'Could not load account holders',
    failedToLoadPaymentSummaries: 'Could not load payment summaries',
    importComplete: 'Import complete',
    importFailed: 'Import failed',
    statementSentSuccessfully: 'Statement sent successfully',
    failedToSendStatement: 'Could not send statement',
    failedToSendStatements: 'Could not send statements',
    confirmSendAllStatements: 'Are you sure you want to send statements to all account holders?',
    userUpdatedSuccessfully: 'User updated successfully',
    userCreatedSuccessfully: 'User created successfully',
    failedToSaveUser: 'Could not save user',
    cannotDeleteOwnAccount: 'You cannot delete your own account',
    confirmDeleteUser: 'Are you sure you want to delete this user?',
    userDeletedSuccessfully: 'User deleted successfully',
    failedToDeleteUser: 'Could not delete user',
    paymentUpdatedSuccessfully: 'Payment updated successfully',
    paymentCreatedSuccessfully: 'Payment created successfully',
    paymentsCreatedSuccessfully: 'Payments created successfully',
    addPaymentToList: 'Add payment to list',
    paymentAddedToList: 'Payment added to list',
    failedToSavePayment: 'Could not save payment',
    paymentAmountMustBeGreaterThanZero: 'Payment amount cannot be zero',
    addPaymentToListBeforeSaving: 'Add at least one payment to the list before saving',
    cancelAddPaymentConfirmMessage: 'Are you sure you want to cancel? Your unsaved payments will be lost.',
    discardChanges: 'Discard changes',
    keepEditing: 'Keep editing',
    paymentDeletedSuccessfully: 'Payment deleted successfully',
    failedToDeletePayment: 'Could not delete payment',
    deletePaymentTitle: 'Delete Payment',
    deletePaymentConfirmMessage: 'Are you sure you want to delete this payment? This action cannot be undone.',
    savingPaymentChanges: 'Saving payment changes',
    creatingPayment: 'Creating payment',
    deletingPayment: 'Deleting payment',
    overallLastThreeMonths: 'Overall Last 3 Months',
    totalThreeMonths: 'Total (3 Months)',
    savingAction: 'Saving...',
    selectDatePlaceholder: 'Click to select date',
    selectTypeOption: 'Select a type',
    allFarmWorkTypesExistForMonth: 'All farm work types already exist for this month.',
    selectedMonths: 'Selected Months',
    addSelectedMonth: 'Add Selected Month',
    startMonthRecurringMonthsLabel: 'Start month and recurring months',
    addThreeMonths: 'Add 3 months',
    addSixMonths: 'Add 6 months',
    addTwelveMonths: 'Add 12 months',
    clearAll: 'Clear all',
    noMonthsSelected: 'No months selected.',
    addSelectedMonthHelp: 'Use the month picker above, then click "Add Selected Month" for each month you want to create.',
    recurringMonthsQuickHelp: 'Choose a start month, then add one month or use the quick options for recurring expenses.',
    skippedMonthsPrefix: 'Skipped',
    monthSingularLabel: 'month',
    monthsPluralLabel: 'months',
    skippedMonthsAllTypesExistSuffix: 'because all expense types already exist',
    skippedMonthsTypeExistsSuffix: 'because this expense type already exists',
    removedMonthsPrefix: 'Removed',
    removedMonthsNoAvailableTypesSuffix: 'with no available expense types.',
    processedMonthsPrefix: 'Processed',
    processedMonthsCreatedSuffix: 'created',
    openingContributorsForFirstCreatedExpense: 'Multiple expenses were created. Opening Add Contributor for the first created expense.',
    fullyCovered: 'Fully Covered',
    addContributor: 'Add Contributor',
    saveAndAddContributor: 'Save and Add Contributor',
    name: 'Name',
    createdDateLabel: 'Created Date',
    farmWorkLabel: 'Expense',
    totalCostLabel: 'Total Cost',
    shortfallLabel: 'Shortfall',
    outstandingCostLabel: 'Cost Shortfall',
    maximumAllowedLabel: 'Maximum allowed',
    noFarmWorkCostsForMonthYet: 'No farm work costs for this month yet.',
    farmWorkCostsCoveredSurplusLabel: 'Farm work costs are covered. Surplus:',
    farmWorkCostsShortfallLabel: 'Farm work costs are not fully covered. Shortfall:',
    deleteFarmWorkTypeTitle: 'Delete Farm Work Type',
    deleteFarmWorkTypeConfirmMessage: 'Are you sure you want to delete this farm work type? This action cannot be undone.',
    deleteFarmWorkTitle: 'Delete Farm Work',
    deleteFarmWorkConfirmMessage: 'Are you sure you want to delete this farm work item? This action cannot be undone.',
    removeContributionTitle: 'Remove Contribution',
    removeContributionConfirmMessage: 'Are you sure you want to remove this contribution? This action cannot be undone.',
    accountHolderUpdatedSuccessfully: 'Account holder updated successfully',
    accountHolderCreatedSuccessfully: 'Account holder created successfully',
    failedToSaveAccountHolder: 'Could not save account holder',
    fieldRequired: 'This field is required',
    validEmailRequired: 'Please enter a valid email address',
    pleaseCompleteRequiredFields: 'Please complete all required fields',
    accountHolderDeletedSuccessfully: 'Account holder deleted successfully',
    failedToDeleteAccountHolder: 'Could not delete account holder',
    deleteAccountHolderTitle: 'Delete Account Holder',
    deleteAccountHolderConfirmMessage: 'Are you sure you want to delete this account holder? This action cannot be undone.',
    activateAccountHolderTitle: 'Activate Account Holder',
    deactivateAccountHolderTitle: 'Deactivate Account Holder',
    activateAccountHolderConfirmMessage: 'Are you sure you want to activate this account holder? This action can be reversed.',
    deactivateAccountHolderConfirmMessage: 'Are you sure you want to deactivate this account holder? This action can be reversed.',
    accountHolderActivatedSuccessfully: 'Account holder activated successfully',
    accountHolderDeactivatedSuccessfully: 'Account holder deactivated successfully',
    failedToActivateAccountHolder: 'Could not activate account holder',
    failedToDeactivateAccountHolder: 'Could not deactivate account holder',
    creditNotes: 'Credits',
    notes: 'Credits',
    addCreditNote: 'Add Credit',
    addNote: 'Add Credit',
    creditNoteAmount: 'Amount',
    noteAmount: 'Amount',
    creditNoteDescription: 'Description',
    creditNoteReason: 'Reason',
    creditNoteHistory: 'Credit Note History',
    activeCreditNotesTotal: 'Total credit amount',
    totalNotesAmount: 'Total credit amount',
    noCreditNotes: 'No credits found',
    noNotesFound: 'No credits found',
    noCreditNoteHistory: 'No credit note history found',
    creditNoteAddedSuccessfully: 'Credit added successfully',
    noteAddedSuccessfully: 'Credit added successfully',
    failedToAddCreditNote: 'Could not add credit',
    failedToAddNote: 'Could not add credit',
    creditNoteUpdatedSuccessfully: 'Credit updated successfully',
    noteUpdatedSuccessfully: 'Credit updated successfully',
    failedToUpdateCreditNote: 'Could not update credit',
    failedToUpdateNote: 'Could not update credit',
    deleteCreditNoteTitle: 'Delete Credit',
    deleteNoteTitle: 'Delete Credit',
    deleteCreditNoteConfirmMessage: 'Are you sure you want to delete this credit? This action cannot be undone.',
    deleteNoteConfirmMessage: 'Are you sure you want to delete this credit? This action cannot be undone.',
    creditNoteDeletedSuccessfully: 'Credit deleted successfully',
    noteDeletedSuccessfully: 'Credit deleted successfully',
    failedToDeleteCreditNote: 'Could not delete credit',
    failedToDeleteNote: 'Could not delete credit',
    voidCreditNote: 'Void Credit Note',
    voidCreditNoteConfirmMessage: 'Are you sure you want to void this credit note? This action cannot be undone.',
    creditNoteVoidedSuccessfully: 'Credit voided successfully',
    failedToVoidCreditNote: 'Could not void credit',
    action: 'Action',
    changedAt: 'Changed At',
    changedBy: 'Changed By',
    activate: 'Activate',
    deactivate: 'Deactivate',
    confirm: 'Confirm',
    phoneNumber: 'Phone Number',
    address: 'Address',
    keywords: 'Keywords',
    autoMapKeywordsLabel: 'Auto-map Keywords (comma-separated)',
    autoMapKeywordsPlaceholder: 'e.g. john smith, rent, fees',
    autoMapKeywordsHelper: 'These keywords help statement transaction auto-mapping.',
    farmWorkTypeUpdatedSuccessfully: 'Farm work type updated successfully',
    farmWorkTypeCreatedSuccessfully: 'Farm work type created successfully',
    farmWorkTypesCreatedSuccessfully: 'Expense types saved successfully',
    toBeAdded: 'To be added',
    noExpenseTypesStaged: 'No expense types added yet. Enter a name above and click Add.',
    addRow: 'Add Row',
    addAnotherExpenseType: 'Add Another',
    removeExpenseType: 'Remove',
    failedToSaveFarmWorkType: 'Could not save farm work type',
    farmWorkTypeDeletedSuccessfully: 'Farm work type deleted successfully',
    failedToDeleteFarmWorkType: 'Could not delete farm work type',
    expenseTypeNameRequired: 'Expense type name is required',
    expenseCostMustBeGreaterThanZero: 'Expense cost must be greater than zero',
    failedValidateAvailableTypesMonth: 'Could not validate available farm work types for month',
    pleaseSelectFarmWorkType: 'Please select a farm work type',
    farmWorkUpdatedSuccessfully: 'Farm work updated successfully',
    noSelectedMonthsHaveAvailableTypes: 'No selected months have available farm work types.',
    failedToSaveFarmWork: 'Could not save farm work',
    farmWorkDeletedSuccessfully: 'Farm work deleted successfully',
    failedToDeleteFarmWork: 'Could not delete farm work',
    farmWorkAlreadyFullyCovered: 'This farm work item is already fully covered.',
    contributionAmountMustBeGreaterThanZero: 'Contribution amount must be greater than zero',
    amountCannotBeNegative: 'Amount cannot be negative',
    pleaseSelectAccountHolder: 'Please select an account holder',
    pleaseSelectAccountHolderForContribution: 'Please select an account holder for the contributor.',
    contributionCannotExceedRemainingCost: 'Contribution cannot exceed remaining cost:',
    contributionCannotExceedOutstandingCost: 'Contribution cannot exceed outstanding cost:',
    contributionCannotExceedAllowableAmount: 'Contribution cannot exceed allowable amount:',
    contributorsCannotExceedExpenseCost: 'Total contributor amounts cannot exceed the expense cost.',
    contributionAddedSuccessfully: 'Contribution added successfully',
    failedToAddContribution: 'Could not add contribution',
    contributionRemovedSuccessfully: 'Contribution removed successfully',
    failedToRemoveContribution: 'Could not remove contribution',
    contributionUpdatedSuccessfully: 'Contribution updated successfully',
    failedToUpdateContribution: 'Could not update contribution',
    optional: '(Optional)',
    selectAccountHolder: 'Select account holder',
    selectedContributors: 'Selected contributors:',
    totalContributionsCost: 'Total contributions',
    remaining: 'Remaining',
    accountHolderAlreadyAdded: 'This account holder has already been added',
    expenseTypeAlreadyAdded: 'This expense type has already been added',
    contributorAddedToList: 'Contributor added to list',
    expenseTypeAddedToList: 'Expense type added to list',
    linkedToContributors: 'Linked to contributors',
    cannotDeleteExpenseTypeWithContributors: 'This expense type cannot be deleted because it is linked to contributor entries.',
    expenseTypeDeleteBlockedTooltip: 'Cannot delete: linked to contributor entries',
    contributor: 'contributor',
    contributorPlural: 'contributors',
    addedSuccessfully: 'added successfully',
    failedToAddContributors: 'Could not add contributors',
    enterDescription: 'Enter description (optional)',
    overpaid: 'overpaid',
    profileTitle: 'User Profile',
    profilePreferences: 'Localization Preferences',
    dashboardSettingsTitle: 'Admin System Settings',
    dashboardSettingsSubtitle: 'Manage system defaults, transaction automapper behavior, and trust account maintenance.',
    couldNotLoadDashboardSettingsData: 'Could not load dashboard settings data',
    dashboardDefaultsSavedSuccessfully: 'Dashboard defaults saved successfully',
    displayDefaults: 'Display Defaults',
    defaultRowsPerPage: 'Default rows per page',
    showSystemFeesChargesByDefault: 'Show account fees/charges by default in transaction table',
    saveDisplayDefaults: 'Save display defaults',
    transactionAutomapperKeywords: 'Transaction Automapper Keywords',
    transactionAutomapperKeywordsHelper: 'Configure keyword matching for account fee/charge categories used by extraction automapper.',
    savingsKeywordsLimitedCategories: 'Savings mapping supports only two categories: Account Fees and General.',
    keywordChanges: 'Keyword changes',
    categoryPending: 'category pending',
    categoriesPending: 'categories pending',
    saveAllKeywordChanges: 'Save all keyword changes',
    savingAllKeywordChanges: 'Saving all...',
    noKeywordChangesToSave: 'No keyword changes to save',
    savedAllKeywordChanges: 'Saved all keyword changes',
    couldNotSaveAllKeywordChanges: 'Could not save all keyword changes',
    unsavedChanges: 'Unsaved changes',
    allChangesSaved: 'All changes saved',
    unsaved: 'Unsaved',
    addOrRemoveKeywordsHint: 'Add or remove keywords below, then save to apply changes.',
    noKeywordsConfigured: 'No keywords configured',
    enterKeywordsCommaSeparated: 'Enter keyword(s), comma separated',
    add: 'Add',
    cancelAddFarmWorkConfirmMessage: 'Are you sure you want to cancel? Your unsaved expense details will be lost.',
    cancelAddFarmWorkTypeConfirmMessage: 'Are you sure you want to cancel? Your unsaved expense type changes will be lost.',
    cancelAddEquipmentHiringConfirmMessage: 'Are you sure you want to cancel? Your unsaved equipment hiring details will be lost.',
    activeKeywords: 'Active keywords',
    none: 'None',
    total: 'Total',
    incoming: 'Incoming',
    outgoing: 'Outgoing',
    net: 'Net',
    category: 'Category',
    accountNumberLabel: 'Account #',
    statementAccountTypeLabel: 'Account type:',
    statementSourceCurrentPreview: 'Statement source: current preview (unsaved)',
    statementSourceLastImport: 'Statement source: last import',
    noStatementAccountInfoTitle: 'No statement account details yet',
    noStatementAccountInfoMessage: 'Import a trust account statement to populate account number, type and period.',
    importStatementToPopulateSummary: 'Import a statement to populate balances and account summary.',
    availableBalanceLabel: 'Available:',
    unclearedChequesLabel: 'Uncleared cheques:',
    lastImportedLabel: 'Last imported:',
    extractingTransactions: 'Extracting transactions…',
    statementExtractionInProgressHint: 'Reading and matching transactions from your statement. This usually takes a few seconds.',
    savingTransactions: 'Saving transactions…',
    statementSavingInProgressHint: 'Saving reviewed transactions and updating your trust account summary.',
    statementProcessingVisibilityHint: 'Please keep this tab open until processing completes.',
    reviewModalSavingVisibilityHint: 'Please wait while mappings and balances are being finalized.',
    closeImportConfirmTitle: 'Cancel import?',
    closeImportConfirmMessage: 'You have an import in progress. Closing now will discard all extracted transactions. Are you sure you want to close?',
    closeImportConfirmButton: 'Yes, discard import',
    importStatementDropzone: 'Import Statement (PDF only) — click or drag and drop',
    createAccountHolderBeforeStatementImport: 'No account holders found. Create an account holder before importing a statement.',
    configureAutomapperKeywords: 'Configure automapper keywords',
    showSystemFeesChargesInTransactions: 'Show account fees/charges in transactions',
    noTrustAccountTransactionsForFilters: 'No trust account transactions found for the selected filters',
    noAccountHolderSummaryForFilters: 'No account holder summary data available for the selected filters',
    noSystemFeeChargeCategoriesForFilters: 'No account fee or charge categories found for the selected filters',
    unknown: 'Unknown',
    trustAccountTransactions: 'Trust Account Transactions',
    selectMonth: 'Select month',
    showing: 'Showing',
    of: 'of',
    accountHolderSummary: 'Account Holder Summary',
    accountHolderSummarySubtitle: 'Incoming and outgoing only, excluding account fee transactions',
    recentTransactions: 'Recent Transactions',
    transactionSummary: 'Transaction Summary',
    accountOverviewSection: 'Account Overview',
    expenseSummarySection: 'Expense Summary',
    manageArea: 'Manage Area',
    manageAreaSubtitle: 'Choose what you want to manage below',
    expensesTabSubtitle: 'Track expenses, contributions, and coverage',
    equipmentHiring: 'Equipment Hiring',
    equipmentHiringTabSubtitle: 'Manage hire costs, income, and recipients',
    farmWorkAndEquipmentTitle: 'Farm Work & Equipment',
    expenseEquipmentSectionSwitcherAria: 'Expense and equipment section switcher',
    expenseEquipmentViewsAria: 'Expense and equipment views',
    activeSection: 'Active Section',
    activeSectionFarmWorkSubtitle: 'Expense records and contributor tracking',
    activeSectionEquipmentSubtitle: 'Hire costs, income sharing, and allocations',
    addEquipmentHiring: 'Add Equipment Hiring',
    editEquipmentHiring: 'Edit Equipment Hiring',
    equipmentHiringItemsTitle: 'Equipment Hiring Items',
    noEquipmentHiringEntriesForSelectedMonth: 'No equipment hiring entries found for selected month',
    equipmentLabel: 'Equipment',
    equipmentPlaceholder: 'e.g. Tractor / Harvester / Trailer',
    selectEquipmentDatePlaceholder: 'Select equipment date',
    equipmentRowAriaPrefix: 'Equipment row',
    incomeRecipients: 'Income Recipients',
    incomeRecipientsCredits: 'Income Recipients (credits)',
    noContributorsYet: 'No contributors yet',
    noIncomeRecipientsYet: 'No income recipients yet',
    incomeRecipient: 'Income Recipient',
    addIncomeRecipient: 'Add Income Recipient',
    incomeShare: 'Income Share',
    clickToRemoveLabelPrefix: 'Click to remove',
    equipmentRequired: 'Equipment is required.',
    dateRequired: 'Date is required.',
    pleaseSelectValidDate: 'Please select a valid date.',
    equipmentHiringUpdatedSuccessfully: 'Equipment hiring updated successfully.',
    equipmentHiringCreatedSuccessfully: 'Equipment hiring created successfully.',
    failedToSaveEquipmentHiring: 'Failed to save equipment hiring.',
    deleteEquipmentHiringTitle: 'Delete Equipment Hiring',
    deleteEquipmentHiringConfirmMessage: 'Are you sure you want to delete this equipment hiring entry?',
    equipmentHiringDeletedSuccessfully: 'Equipment hiring deleted successfully.',
    failedToDeleteEquipmentHiring: 'Failed to delete equipment hiring.',
    equipmentContributorUpdatedSuccessfully: 'Equipment contributor updated successfully.',
    equipmentContributorAddedSuccessfully: 'Equipment contributor added successfully.',
    failedToUpdateEquipmentContributor: 'Failed to update equipment contributor.',
    failedToAddEquipmentContributor: 'Failed to add equipment contributor.',
    removeEquipmentContributorTitle: 'Remove Equipment Contributor',
    removeEquipmentContributorConfirmPrefix: 'Are you sure you want to remove',
    removeEquipmentContributorConfirmSuffix: 'from this equipment hiring entry?',
    equipmentContributorRemovedSuccessfully: 'Equipment contributor removed successfully.',
    failedToRemoveEquipmentContributor: 'Failed to remove equipment contributor.',
    incomeRecipientUpdatedSuccessfully: 'Income recipient updated successfully.',
    incomeRecipientAddedSuccessfully: 'Income recipient added successfully.',
    failedToUpdateIncomeRecipient: 'Failed to update income recipient.',
    failedToAddIncomeRecipient: 'Failed to add income recipient.',
    removeIncomeRecipientTitle: 'Remove Income Recipient',
    removeIncomeRecipientConfirmPrefix: 'Are you sure you want to remove',
    removeIncomeRecipientConfirmSuffix: 'from income recipients?',
    incomeRecipientRemovedSuccessfully: 'Income recipient removed successfully.',
    failedToRemoveIncomeRecipient: 'Failed to remove income recipient.',
    totalEquipmentCost: 'Total Equipment Cost',
    totalContributorsLabel: 'Total Contributors',
    incomeGenerated: 'Income Generated',
    hoursHired: 'Hours Hired',
    savingsAccountStats: 'Savings Account Stats',
    savingsInterestVsSystemFees: 'Savings interest vs account fees/charges',
    savingsInterestVsSystemFeesLastThreeMonths: 'Savings Interest vs Account Fees/Charges (Last 3 Months)',
    interestGain: 'Interest Gain',
    interestIncome: 'Interest Income',
    netAfterSystemCharges: 'Net After System Charges',
    totalBalance: 'Total Balance',
    totalIncome: 'Total Income',
    totalExpenses: 'Total Expenses',
    netMovement: 'Net Movement',
    systemFeesChargesOverview: 'Account Fees & Charges Overview',
    systemFeeChargeLabel: 'Account fee/charge',
    sendingStatementEmail: 'Sending statement email',
    noPaymentSummariesFoundForMonth: 'No payment summaries found for',
    reviewExtractedRowsPrefix: 'Review',
    reviewExtractedRowsSuffix: 'extracted rows.',
    transactionsReadyToSave: 'transaction(s) are ready to save',
    autoMappedByExtraction: 'auto-mapped by extraction',
    alreadyExistsMayBeSkippedOrUpdated: 'already exist and may be skipped unless updated',
    couldNotExtractTransactionsFromFile: 'Could not extract transactions from this file. Confirm the file is a valid trust account CSV or PDF statement and try again.',
    markedTransactionIgnoredOnSave: 'Marked transaction to be ignored on save.',
    updatedExistingMappingFrom: 'Updated existing mapping from',
    previousAccountHolder: 'previous account holder',
    to: 'to',
    removedExistingMappingForTransaction: 'Removed existing mapping for this transaction.',
    mappedTransactionTo: 'Mapped transaction to',
    transactionsStillNeedAccountHolderMapping: 'transaction(s) still need account holder mapping',
    savingReviewedTransactions: 'Saving reviewed transactions. This may take a few seconds...',
    reviewExtractedTransactions: 'Review extracted transactions',
    verifyTransactionMappingsBeforeSaving: 'Verify each transaction and confirm the correct account holder mapping before saving.',
    noPreviewDataAvailable: 'No preview data available.',
    extractionOverviewTitle: 'Extraction overview',
    extractionOverviewSubtitle: 'Confirm header details and review readiness before saving.',
    statementPeriod: 'Statement period',
    statementAccountNumberLabel: 'Account number',
    statementAccountNumberAutoDetectedBadge: 'Auto-detected',
    statementAccountNumberAutoDetectedTooltip: 'Auto-detected from the imported file. Please verify it before saving.',
    enterStatementAccountNumber: 'Enter account number from statement',
    statementAccountNumberHelper: 'If account number is not auto-extracted, enter it here before saving.',
    statementAccountNumberReadOnlyHelper: 'Account number was auto-populated from the statement header.',
    statementAccountNumberEditAction: 'Edit',
    statementAccountNumberLockAction: 'Lock',
    readyToSave: 'Ready to save',
    needsMapping: 'Needs mapping',
    willBeSkipped: 'Will be skipped',
    ignored: 'Ignored',
    invalidRows: 'Invalid rows',
    totalRows: 'Total rows',
    extractionReviewInfoMessage: 'Header and non-transaction rows are excluded from preview. Existing matching transactions are auto-detected and will be skipped unless you change mapping to update them. Use "Ignore this transaction" for rows you do not want imported.',
    selectAccountHolderOption: '-- Select account holder --',
    selectCategoryOption: '-- Select category --',
    savingsSystemFeesOptionLabel: 'Account Fees - bank/service charges',
    savingsGeneralOptionLabel: 'General - interest credits',
    ignoreThisTransaction: 'Ignore this transaction',
    transactionWillBeIgnoredDuringImport: 'This transaction will be ignored during import.',
    autoMappedToSystemFeeCategory: 'Auto-mapped to account fee/charge category',
    willAutoCategorizeOnSaveAs: 'Will auto-categorize on save as',
    matchingExistingTransactionFoundMessage: 'Matching existing transaction found; save will skip it unless mapping changes require an update.',
    validationDetails: 'Validation details',
    invalidRow: 'Invalid row',
    missingRequiredValues: 'Missing required values',
    saveStatementTransactions: 'Save statement transactions',

    chatBotLabel: 'Help',
    chatBotTitle: 'Help & Support',
    chatBotWelcome: 'Hi! How can I help you navigate Savannah Accounts?',
    chatBotTopicsPrompt: 'Select a topic to get started:',
    chatBotBack: '← Back to topics',
    chatBotTopicDashboard: 'Dashboard',
    chatBotTopicDashboardContent: 'The Dashboard shows your trust account balance and activity. Use the account switcher at the top to toggle between Current and Savings accounts. The Monthly Comparison chart displays credits and debits for the last four months. The Recent Transactions panel lists the latest entries for the selected account. You can also import statement transactions from this screen by uploading a file, reviewing the extracted rows, and saving the mapped results.',
    chatBotTopicAccountHolders: 'Account Holders',
    chatBotTopicAccountHoldersContent: 'Account Holders are individuals or entities linked to your trust account. You can add, edit, activate, or deactivate holders from this page. Each holder supports multiple address lines with individual monthly fees. Use Credit Notes to offset outstanding amounts, and send custom emails to selected holders directly from the list.',
    chatBotTopicExpenses: 'Expenses',
    chatBotTopicExpensesContent: 'The Expenses page manages farm work costs and equipment hiring. Start by adding Expense Types, then create monthly expense items. Assign contributors to split costs among account holders. The Equipment Hiring tab tracks hire costs, hours worked, and income generated per account holder.',
    chatBotTopicPayments: 'Payments',
    chatBotTopicPaymentsContent: 'The Payments page (Account Overview) shows all trust account transactions. Add single or batch payments, filter by account holder and date range, and edit or delete existing entries. The Transaction Summary panel totals credits and debits for the selected filters.',
    chatBotTopicStatements: 'Statements',
    chatBotTopicStatementsContent: 'Statements are sent from the Payments page. Choose a date range and trust account, select one or more contributors, then click Send Email to email their statement. You can also print a statement directly from the browser. Each statement includes a balance overview, expense contributions, and bank payment details.',
    chatBotTopicSettings: 'Settings',
    chatBotTopicSettingsContent: 'Settings are accessible to Administrators only. Admin System Settings lets you configure display defaults and transaction automapper keywords. User Management lets you create, edit, or remove system users and set their roles. Trust Account setup is also managed from this area.',
    chatBotInputPlaceholder: 'Ask how to use a page or feature...',
    chatBotSend: 'Send',
    chatBotClear: 'Clear chat',
    chatBotUnknown: 'I could not find a direct answer for that yet. Try asking about Dashboard, Account Holders, Expenses, Payments, Statements, or Settings.',
    chatBotSuggestions: 'Try questions like:',
    chatBotSuggestionOne: 'How do I send statements?',
    chatBotSuggestionTwo: 'How do I add an account holder?',
    chatBotSuggestionThree: 'How do I capture expenses?',
    chatBotSuggestionDashboard: 'How does the Dashboard work?',
    chatBotSuggestionPayments: 'How do I view transactions?',
    chatBotSuggestionSettings: 'How do I configure settings?',
    chatBotSuggestionTrustAccount: 'How do trust accounts work?',
    chatBotSuggestionCreditNote: 'How do I add a credit note?',
    chatBotSuggestionUsers: 'How do I manage users?',
    chatBotMinimizeSuggestions: 'Minimize',
    chatBotShowSuggestions: 'Expand suggestions',
    chatBotDismissSuggestions: 'Dismiss',
    chatBotNavigationLabel: 'Navigation',
    chatBotOpenPage: 'Open page',
    chatBotStepsLabel: 'How-to steps',
    chatBotScreenshotLabel: 'Screenshot guide',
    chatBotDashboardStep1: 'Open Dashboard and select the trust account you want to work with using the account switcher.',
    chatBotDashboardStep2: 'Use the month picker, holder filter, and system transaction toggle to narrow what is displayed.',
    chatBotDashboardStep3: 'In the statement importer panel, upload your bank statement file (PDF or text export).',
    chatBotDashboardStep4: 'Review extracted rows in the preview modal, map each row to an account holder, fix account number if needed, and mark irrelevant rows as ignored.',
    chatBotDashboardStep5: 'Click Save statement transactions, then confirm the imported records appear in Recent Transactions and monthly totals.',
    chatBotAccountHoldersStep1: 'Open Account Holders and click Add New Account Holder to create a new profile.',
    chatBotAccountHoldersStep2: 'Capture holder details, then add address lines with monthly fees and keyword tags used for matching.',
    chatBotAccountHoldersStep3: 'Save the holder and use Edit, Activate, or Deactivate actions from the holder table when updates are needed.',
    chatBotAccountHoldersStep4: 'Open Notes for a holder to add, edit, or remove credit notes that offset outstanding amounts.',
    chatBotAccountHoldersStep5: 'Use Send Custom Email to select recipients, compose the message, and send or keep a draft.',
    chatBotExpensesStep1: 'Open Expenses, choose the month, and switch between Farm Work and Equipment Hiring sections.',
    chatBotExpensesStep2: 'Use Manage Expense Types to add, edit, or remove farm work categories before capturing costs.',
    chatBotExpensesStep3: 'Create a farm work expense, set amount and recurring months, then save the entry.',
    chatBotExpensesStep4: 'Use Add Contributor to assign holder contributions, then edit or remove contributions as needed.',
    chatBotExpensesStep5: 'In Equipment Hiring, capture hire costs, hours, income, and allocate both contributors and income recipients.',
    chatBotPaymentsStep1: 'Open Payments and select the trust account context you want to review.',
    chatBotPaymentsStep2: 'Apply holder and month filters, and use Reset Filters to return to the default view.',
    chatBotPaymentsStep3: 'Use Add New Payment for a single transaction with holder, amount, date, and description.',
    chatBotPaymentsStep4: 'Use Add New Payments to build a draft list for batch capture, then save the queued entries together.',
    chatBotPaymentsStep5: 'Review summary cards and transaction table totals, then open Statements Center for statements and email actions.',
    chatBotStatementsStep1: 'Open Statements Center, choose month and holder filters, and load the statement period you need.',
    chatBotStatementsStep2: 'Expand holder rows to review farm work, fee, and contribution breakdown tables before sending.',
    chatBotStatementsStep3: 'Use Print on a single holder or selected holders to generate browser print-ready statements.',
    chatBotStatementsStep4: 'Use Send Email for one holder or batch email for selected holders after reviewing totals.',
    chatBotStatementsStep5: 'Check KPI cards and contributor status badges to confirm outstanding, partial, or fully paid states.',
    chatBotSettingsStep1: 'Open Settings > Admin System Settings to create and edit trust account details.',
    chatBotSettingsStep2: 'Set default page size and system fee visibility, then save display defaults.',
    chatBotSettingsStep3: 'Switch keyword account type and manage automapper categories by adding, removing, or resetting keywords.',
    chatBotSettingsStep4: 'Save keyword changes and confirm category status badges move from pending to saved.',
    chatBotSettingsStep5: 'Open Settings > User Management to create, edit, and remove users and maintain their access roles.',
  },
  af: {
    appTitle: 'Savannah Rekeninge',
    dashboard: 'Kontroleskerm',
    accountHolders: 'Rekeninghouers',
    accountManagement: 'Rekeningbestuur',
    accountOverview: 'Transaksie Opsomming',
    accountOverviewTitle: 'Rekeningoorsig',
    farmWork: 'Uitgawes',
    payments: 'Betalings',
    statements: 'State',
    settings: 'Instellings',
    more: 'Meer',
    userManagement: 'Gebruikersbestuur',
    profile: 'Profiel',
    dashboardSettings: 'Admin Stelselinstellings',
    language: 'Taal',
    currency: 'Geldeenheid',
    logout: 'Teken Uit',
    loadingApplication: 'Laai toepassing...',
    loading: 'Laai...',
    close: 'Sluit',
    cancel: 'Kanselleer',
    draftSaved: 'Konsep gestoor',
    save: 'Stoor',
    update: 'Dateer Op',
    edit: 'Wysig',
    delete: 'Verwyder',
    remove: 'Verwyder',
    actions: 'Aksies',
    rows: 'Rye',
    active: 'Aktief',
    inactive: 'Onaktief',
    yes: 'Ja',
    no: 'Nee',
    send: 'Stuur',
    selectAll: 'Kies Alles',

    login: 'Teken In',
    registering: 'Registreer...',
    loggingIn: 'Teken in...',
    register: 'Registreer',
    username: 'Gebruikersnaam',
    password: 'Wagwoord',
    email: 'E-pos',
    firstName: 'Voornaam',
    lastName: 'Van',
    confirmPassword: 'Bevestig Wagwoord',
    loginFailed: 'Aanmelding het misluk',
    errorDuringLogin: "'n Fout het tydens aanmelding voorgekom",
    registerFailed: 'Registrasie het misluk',
    errorDuringRegister: "'n Fout het tydens registrasie voorgekom",
    passwordsDoNotMatch: 'Bevestig wagwoord stem nie ooreen nie',
    passwordMinChars: 'Wagwoord moet ten minste 6 karakters lank wees',
    defaultAdminUsernameLabel: 'Verstek admin-gebruikersnaam',
    defaultAdminPasswordLabel: 'Verstek admin-wagwoord',
    dontHaveAccount: 'Het jy nie ’n rekening nie?',
    contactAdminOr: 'Kontak jou administrateur of',
        contactAdminToRegister: 'Kontak jou administrateur om ’n rekening te skep.',
    createNewAccount: 'skep ’n nuwe rekening',
    createAccountSubtitle: 'Skep ’n rekening om toegang te verkry tot jou trustrekening-bestuurstelsel',
    alreadyHaveAccount: 'Het jy reeds ’n rekening?',
    loginHere: 'Teken hier in',
    chooseUsernamePlaceholder: 'Kies ’n gebruikersnaam',
    enterEmailPlaceholder: 'Voer jou e-pos in',
    enterFirstNamePlaceholder: 'Voer jou voornaam in',
    enterLastNamePlaceholder: 'Voer jou van in',
    strongPasswordPlaceholder: 'Voer ’n sterk wagwoord in',
    confirmPasswordPlaceholder: 'Bevestig jou wagwoord',
    atLeast6Chars: 'Ten minste 6 karakters',
    leaveBlankKeepCurrent: 'laat leeg om huidige te behou',
    maintenanceTitle: 'Onder Onderhoud',
    maintenanceMessage: 'Ons voer tans geskeduleerde onderhoud uit.',
    maintenanceExpected: 'Ons verwag om kort daarna weer aanlyn te wees. Dankie vir jou geduld.',
    maintenanceContact: 'Indien jy dringende vrae het, kontak asseblief jou administrateur.',
    userRole: 'Gebruiker',
    adminRole: 'Administrateur',
    english: 'Engels',
    afrikaans: 'Afrikaans',
    currencyUsd: 'USD',
    currencyZar: 'ZAR (R)',

    accountHoldersTitle: 'Rekeninghouers',
    manageAccountHoldersSubtitle: 'Bestuur jou rekeninghouer-rekords en kommunikasie op een plek.',
      selectRecipientBeforeSending: 'Kies asseblief ten minste een ontvanger voordat jy stuur.',
      enterEmailSubject: 'Voer asseblief ’n onderwerp in',
      enterEmailMessage: 'Voer asseblief ’n boodskap in',
      failedToSendCustomEmail: 'Kon nie pasgemaakte e-pos stuur nie. Kontroleer jou verbinding of probeer weer later.',
    addNewAccountHolder: 'Voeg Nuwe Rekeninghouer By',
    noAccountHoldersFound: 'Geen rekeninghouers gevind nie',
    loadingAccountHolders: 'Laai rekeninghouers...',
    addresses: 'Adresse',
    linkedAddresses: 'Gekoppelde Adresse',
    linkedAddressesWithFees: 'Adreslyne (Maandelikse Fooi)',
    monthlyFeeHistory: 'Maandelikse Fooigeskiedenis',
    effectiveFromMonth: 'Effektief Vanaf Maand',
    changedAt: 'Gewysig Op',
    addAddress: 'Voeg Adres By',
    monthlyFee: 'Maandelikse Fooi',
    addressLineCannotBeBlank: 'Adreslyn mag nie leeg wees nie',
    duplicateAddressesNotAllowed: 'Duplikaat adresse word nie toegelaat nie',
    duplicateEmailsNotAllowed: 'Duplikaat e-posadresse word nie toegelaat nie',
    addressGroups: 'adresgroepe',
    noAddressesAssigned: 'Geen adres toegewys nie',
    customEmailSent: 'Jou e-pos is na die geselekteerde rekeninghouer(s) gestuur.',
    composeSendEmail: 'Stel E-pos Op en Stuur',
    sendingToLabel: 'Stuur aan:',
    noRecipientsSelected: 'Geen ontvangers gekies nie',
    addRecipientPlaceholder: 'Voeg ontvanger by...',
    subjectRequired: 'Onderwerp *',
    emailSubjectPlaceholder: 'Voer ’n duidelike, bondige onderwerplyn in...',
    messageRequired: 'Boodskap *',
    emailGreeting: 'Hallo',
    emailFooterNoReply: 'Hierdie e-pos is via Savannah Rekeninge gestuur. Moet asseblief nie direk op hierdie boodskap antwoord nie.',

    paymentsTitle: 'Betalings',
    addNewPayment: 'Voeg Nuwe Betaling By',
    addNewPayments: 'Voeg Nuwe Betalings By',
    loadingPayments: 'Laai betalings...',
    noPaymentsFound: 'Geen betalings gevind nie',
    filters: 'Filters',
    month: 'Maand',
    reset: 'Herstel',
    amount: 'Bedrag',
    description: 'Beskrywing',
    paymentDate: 'Betaaldatum',
    totalAmount: 'Totale Bedrag',
    transactions: 'Transaksies',
    accountHolder: 'Rekeninghouer',
    allAccountHolders: 'Alle Rekeninghouers',

    statementsTitle: 'Betalingstate',
    sendAllStatements: 'Stuur Alle State',
    sendingStatements: 'Stuur state...',
    sendingAction: 'Stuur...',
    sendEmail: 'Stuur E-pos',
    print: 'Druk',
    sending: 'Stuur...',
    view: 'Bekyk',
    period: 'Tydperk',
    statementDetails: 'Staatbesonderhede',
    detailedTransactions: 'Gedetailleerde Transaksies',
    monthlyAccountFees: 'Maandelikse Rekeningfooie',
    accountFeeBreakdown: 'Rekeningfooi-uiteensetting',
    includesMonthlyAccountFees: 'Sluit maandelikse rekeningfooie in',
    bankDetails: 'Bankbesonderhede',
    accountName: 'Rekeningnaam',
    bankNameBranchCode: 'Banknaam - Tak Kode',
    accountTypeAccountNumber: 'Rekening Tipe - Rekeningnommer',
    accountNumber: 'Rekeningnommer',
    branchCode: 'Absa Bank - Tak Kode',
    paymentReference: 'Betalingsverwysing',
    paymentReferenceDescription: 'Voorletters, van en erf/plot nommer',
    sendSelectedEmails: 'Stuur E-pos(se)',
    confirmSendSelectedStatements: 'Stuur state na die geselekteerde bydraers?',
    selectAllContributors: 'Kies alle bydraers',
    selectContributor: 'Kies bydraer',
    selectedContributorsCount: 'Geselekteerde bydraers',
    selectContributorBeforeSending: 'Kies asseblief ten minste een bydraer voordat jy stuur.',
    selectContributorBeforePrinting: 'Kies asseblief ten minste een bydraer voordat jy druk.',
    sendStatementsToFiltered: 'Stuur Gefilterde State',
    confirmSendStatementsToFiltered: 'Stuur state slegs na die gekose rekeninghouer?',
    filtered: 'Gefilterd',
    farmWorkMonthlyOverview: 'Uitgawes Maandoorsig',
    farmWorkContributionOverview: 'Uitgawes Bydrae Oorsig',
    requiredContribution: 'Vereiste Bydrae',
    totalRequiredContribution: 'Totale Vereiste Bydrae',
    appliedToFarmWork: 'Toegepas op Uitgawes',
    amountPaidFromStatements: 'Bedrag Betaal (State)',
    statementTotalForMonth: 'Staat Totaal (Maand)',
    netStatementMovement: 'Netto Staatbeweging',
    outstandingFees: 'Uitstaande Fooie',
    outstanding: 'Uitstaande',
    overpaidAmount: 'Oorbetaalde Bedrag',
    overpaidShort: 'Oorbetaal',
    estimatedPaid: 'Geskatte Betaal',
    totalCredits: 'Totale Krediete',
    totalDebits: 'Totale Debiete',
    creditsVsDebits: 'Krediete / Debiete',
    contributorsStatus: 'Bydraersstatus',
    notPaid: 'Nie Betaal Nie',
    paidInFull: 'Volledig Betaal',
    withOutstanding: 'Met Uitstaande',
    partiallyPaid: 'Gedeeltelik Betaal',
    noContributionDue: 'Geen Bydrae Verskuldig',
    holderHasOutstandingFees: 'Hierdie rekeninghouer het uitstaande uitgawefooie',
    costCoverageGap: 'Kostedekking Gaping',
    noFarmWorkForSelectedMonth: 'Geen uitgawe-items vir die geselekteerde maand gevind nie',
    noHolderContributionOrPaymentsForMonth: 'Geen rekeninghouerbydraes of betalings gevind vir',
    allocationMethodLabel: 'Toewysingsmetode',
    allocationMethodOldestFirst: 'Betalings word aan uitgaweverpligtinge oudste-eerste toegeken (volgens skeppingsdatum), dan volgens item-ID.',
    date: 'Datum',
    status: 'Status',
    trustAccount: 'Trust Rekening',
    addTrustAccount: 'Voeg Rekening By',
    addTrustAccountTitle: 'Voeg Rekening By',
    accountType: 'Rekening Tipe',
    currentAccountType: 'Lopende',
    savingsAccountType: 'Spaar',
    activeAccount: 'Aktiewe Rekening',
    switchAccounts: 'Wissel Rekeninge',
    switchAccountsPrompt: 'Kies watter rekening jy op hierdie blad wil sien.',
    trustBankNameInputLabel: 'Banknaam',
    trustAccountNameLabel: 'Rekeningnaam',
    trustAccountNumberInputLabel: 'Rekeningnommer',
    trustBranchCodeInputLabel: 'Tak Kode',
    oneAccountPerTypeAllowed: 'Slegs een rekening per tipe word toegelaat.',
    allTrustAccountTypesConfigured: 'Lopende en spaarrekeninge bestaan reeds.',
    trustAccountAddedSuccessfully: 'Trust rekening suksesvol bygevoeg',
    trustAccountUpdatedSuccessfully: 'Trust rekening suksesvol opgedateer',
    couldNotAddTrustAccount: 'Kon nie trust rekening byvoeg nie',
    couldNotUpdateTrustAccount: 'Kon nie trust rekening opdateer nie',
    accountTypeAlreadyExistsError: '’n Rekening met hierdie tipe bestaan reeds.',
    accountNameAlreadyExistsError: '’n Rekening met hierdie naam bestaan reeds. Rekeningname moet uniek wees.',
    accountNumberAlreadyExistsError: '’n Rekening met hierdie nommer bestaan reeds. Rekeningnommers moet uniek wees.',
    trustAccountManagement: 'Rekening Bestuur',
    trustAccountManagementSubtitle: 'Slegs administrateur-onderhoud vir trustrekening-opstelling.',
    trustAccountManagementAdminOnly: 'Slegs administrateurs kan trustrekeninge bestuur.',
    existingTrustAccounts: 'Bestaande Trust Rekeninge',
    noTrustAccountsConfigured: 'Nog geen trustrekeninge opgestel nie.',

    userManagementTitle: 'Gebruikersbestuur',
    usersLabel: 'Gebruikers',
    addNewUser: 'Voeg Nuwe Gebruiker By',
    noUsersFound: 'Geen gebruikers gevind nie',
    loadingUsers: 'Laai gebruikers...',
    role: 'Rol',
    created: 'Geskep',
    lastLogin: 'Laaste Aanmelding',
    never: 'Nooit',
    createNewUser: 'Skep Nuwe Gebruiker',
    editUser: 'Wysig Gebruiker',

    farmWorkManagement: 'Uitgawebestuur',
    farmWorkItems: 'Uitgawe-items',
    farmWorkTypes: 'Uitgawetipes',
    addFarmWork: 'Voeg Uitgawe By',
    addFarmWorks: 'Voeg Uitgawes By',
    addFarmWorkType: 'Voeg Uitgawetipes By',
    addExpenseModalHelperTitle: 'Vinnige uitgawe-inskrywing',
    addExpenseModalHelperText: 'Stoor eers die uitgawe, en voeg net bydraers by indien nodig. Bydraers is opsioneel en kan later bygevoeg word.',
    selectMonthYear: 'Datum',
    noFarmWorkItemsForMonth: 'Geen uitgawe-items vir gekose maand/jaar nie',
    noFarmWorkTypesFound: 'Geen uitgawetipes gevind nie',
    contributors: 'Bydraers',
    contributions: 'Bydraes',
    contributionsDetails: 'Bydraebesonderhede',
    noContributionsYet: 'Nog geen bydraes aangeteken nie',
    cost: 'Koste',
    type: 'Tipe',
    coverage: 'Dekking',
    totalContributions: 'Totale Bydraes',
    totalFarmWorkCost: 'Totale Uitgawes',

    dashboardTitle: 'Trust Rekening Dashboard',
    allAccounts: 'Alle Rekeninge',
    trackedAccounts: 'opgespoorde rekeninge',
    monthlyComparison: 'Maandelikse Vergelyking',
    lastFourMonths: 'Laaste 4 Maande',
    mainTrustAccount: 'Hoof Trust Rekening',
    loadingDashboard: 'Laai dashboard...',
    failedToLoadData: 'Kon nie data laai nie',
    couldNotLoadData: 'Kon nie data laai nie',
    failedToLoadDashboardData: 'Kon nie dashboard-data laai nie',
    failedToLoadUsers: 'Kon nie gebruikers laai nie',
    failedToLoadAccountHolders: 'Kon nie rekeninghouers laai nie',
    failedToLoadPaymentSummaries: 'Kon nie betalingsopsommings laai nie',
    importComplete: 'Invoer voltooi',
    importFailed: 'Invoer het misluk',
    statementSentSuccessfully: 'Staat suksesvol gestuur',
    failedToSendStatement: 'Kon nie staat stuur nie',
    failedToSendStatements: 'Kon nie state stuur nie',
    confirmSendAllStatements: 'Is jy seker jy wil state aan alle rekeninghouers stuur?',
    userUpdatedSuccessfully: 'Gebruiker suksesvol opgedateer',
    userCreatedSuccessfully: 'Gebruiker suksesvol geskep',
    failedToSaveUser: 'Kon nie gebruiker stoor nie',
    cannotDeleteOwnAccount: 'Jy kan nie jou eie rekening verwyder nie',
    confirmDeleteUser: 'Is jy seker jy wil hierdie gebruiker verwyder?',
    userDeletedSuccessfully: 'Gebruiker suksesvol verwyder',
    failedToDeleteUser: 'Kon nie gebruiker verwyder nie',
    paymentUpdatedSuccessfully: 'Betaling suksesvol opgedateer',
    paymentCreatedSuccessfully: 'Betaling suksesvol geskep',
    paymentsCreatedSuccessfully: 'Betalings suksesvol geskep',
    addPaymentToList: 'Voeg betaling by lys',
    paymentAddedToList: 'Betaling by lys gevoeg',
    failedToSavePayment: 'Kon nie betaling stoor nie',
    paymentAmountMustBeGreaterThanZero: 'Betalingsbedrag kan nie nul wees nie',
    addPaymentToListBeforeSaving: 'Voeg ten minste een betaling by die lys voor stoor',
    cancelAddPaymentConfirmMessage: 'Is jy seker jy wil kanselleer? Jou ongebergde betalings sal verlore gaan.',
    discardChanges: 'Verwerp veranderings',
    keepEditing: 'Hou aan redigeer',
    paymentDeletedSuccessfully: 'Betaling suksesvol verwyder',
    failedToDeletePayment: 'Kon nie betaling verwyder nie',
    deletePaymentTitle: 'Verwyder Betaling',
    deletePaymentConfirmMessage: 'Is jy seker jy wil hierdie betaling verwyder? Hierdie aksie kan nie ongedaan gemaak word nie.',
    savingPaymentChanges: 'Stoor betalingsveranderinge',
    creatingPayment: 'Skep betaling',
    deletingPayment: 'Verwyder betaling',
    overallLastThreeMonths: 'Algeheel Laaste 3 Maande',
    totalThreeMonths: 'Totaal (3 Maande)',
    savingAction: 'Stoor...',
    selectDatePlaceholder: 'Klik om datum te kies',
    selectTypeOption: 'Kies ’n tipe',
    allFarmWorkTypesExistForMonth: 'Alle plaaswerktipes bestaan reeds vir hierdie maand.',
    selectedMonths: 'Geselekteerde Maande',
    addSelectedMonth: 'Voeg Geselekteerde Maand By',
    startMonthRecurringMonthsLabel: 'Beginmaand en herhalende maande',
    addThreeMonths: 'Voeg 3 maande by',
    addSixMonths: 'Voeg 6 maande by',
    addTwelveMonths: 'Voeg 12 maande by',
    clearAll: 'Maak alles skoon',
    noMonthsSelected: 'Geen maande geselekteer nie.',
    addSelectedMonthHelp: 'Gebruik die maandkieser hierbo, klik dan "Voeg Geselekteerde Maand By" vir elke maand wat jy wil skep.',
    recurringMonthsQuickHelp: 'Kies ’n beginmaand, voeg dan een maand by of gebruik die vinnige opsies vir herhalende uitgawes.',
    skippedMonthsPrefix: 'Oorgeslaan',
    monthSingularLabel: 'maand',
    monthsPluralLabel: 'maande',
    skippedMonthsAllTypesExistSuffix: 'omdat alle uitgawetipes reeds bestaan',
    skippedMonthsTypeExistsSuffix: 'omdat hierdie uitgawetipe reeds bestaan',
    removedMonthsPrefix: 'Verwyder',
    removedMonthsNoAvailableTypesSuffix: 'met geen beskikbare uitgawetipes nie.',
    processedMonthsPrefix: 'Verwerk',
    processedMonthsCreatedSuffix: 'geskep',
    openingContributorsForFirstCreatedExpense: 'Veelvuldige uitgawes is geskep. Voeg Bydraer maak oop vir die eerste geskepte uitgawe.',
    fullyCovered: 'Volledig Gedek',
    addContributor: 'Voeg Bydraer By',
    saveAndAddContributor: 'Stoor en Voeg Bydraer By',
    name: 'Naam',
    createdDateLabel: 'Skeppingsdatum',
    farmWorkLabel: 'Uitgawe',
    totalCostLabel: 'Totale Koste',
    shortfallLabel: 'Tekort',
    outstandingCostLabel: 'Kostetekort',
    maximumAllowedLabel: 'Maksimum toegelaat',
    noFarmWorkCostsForMonthYet: 'Nog geen plaaswerkkoste vir hierdie maand nie.',
    farmWorkCostsCoveredSurplusLabel: 'Plaaswerkkoste is gedek. Oorskot:',
    farmWorkCostsShortfallLabel: 'Plaaswerkkoste is nie ten volle gedek nie. Tekort:',
    deleteFarmWorkTypeTitle: 'Verwyder Plaaswerktipe',
    deleteFarmWorkTypeConfirmMessage: 'Is jy seker jy wil hierdie plaaswerktipe verwyder? Hierdie aksie kan nie ongedaan gemaak word nie.',
    deleteFarmWorkTitle: 'Verwyder Plaaswerk',
    deleteFarmWorkConfirmMessage: 'Is jy seker jy wil hierdie plaaswerkitem verwyder? Hierdie aksie kan nie ongedaan gemaak word nie.',
    removeContributionTitle: 'Verwyder Bydrae',
    removeContributionConfirmMessage: 'Is jy seker jy wil hierdie bydrae verwyder? Hierdie aksie kan nie ongedaan gemaak word nie.',
    accountHolderUpdatedSuccessfully: 'Rekeninghouer suksesvol opgedateer',
    accountHolderCreatedSuccessfully: 'Rekeninghouer suksesvol geskep',
    failedToSaveAccountHolder: 'Kon nie rekeninghouer stoor nie',
    fieldRequired: 'Hierdie veld is verpligtend',
    validEmailRequired: 'Voer asseblief ’n geldige e-posadres in',
    pleaseCompleteRequiredFields: 'Voltooi asseblief alle verpligte velde',
    accountHolderDeletedSuccessfully: 'Rekeninghouer suksesvol verwyder',
    failedToDeleteAccountHolder: 'Kon nie rekeninghouer verwyder nie',
    deleteAccountHolderTitle: 'Verwyder Rekeninghouer',
    deleteAccountHolderConfirmMessage: 'Is jy seker jy wil hierdie rekeninghouer verwyder? Hierdie aksie kan nie ongedaan gemaak word nie.',
    activateAccountHolderTitle: 'Aktiveer Rekeninghouer',
    deactivateAccountHolderTitle: 'Deaktiveer Rekeninghouer',
    activateAccountHolderConfirmMessage: 'Is jy seker jy wil hierdie rekeninghouer aktiveer? Hierdie aksie kan omgekeer word.',
    deactivateAccountHolderConfirmMessage: 'Is jy seker jy wil hierdie rekeninghouer deaktiveer? Hierdie aksie kan omgekeer word.',
    accountHolderActivatedSuccessfully: 'Rekeninghouer suksesvol geaktiveer',
    accountHolderDeactivatedSuccessfully: 'Rekeninghouer suksesvol gedeaktiveer',
    failedToActivateAccountHolder: 'Kon nie rekeninghouer aktiveer nie',
    failedToDeactivateAccountHolder: 'Kon nie rekeninghouer deaktiveer nie',
    creditNotes: 'Krediete',
    notes: 'Krediete',
    addCreditNote: 'Voeg Krediet By',
    addNote: 'Voeg Krediet By',
    creditNoteAmount: 'Bedrag',
    noteAmount: 'Bedrag',
    creditNoteDescription: 'Beskrywing',
    creditNoteReason: 'Rede',
    creditNoteHistory: 'Kredietnota Geskiedenis',
    activeCreditNotesTotal: 'Totale kredietbedrag',
    totalNotesAmount: 'Totale kredietbedrag',
    noCreditNotes: 'Geen krediete gevind nie',
    noNotesFound: 'Geen krediete gevind nie',
    noCreditNoteHistory: 'Geen kredietnota geskiedenis gevind nie',
    creditNoteAddedSuccessfully: 'Krediet suksesvol bygevoeg',
    noteAddedSuccessfully: 'Krediet suksesvol bygevoeg',
    failedToAddCreditNote: 'Kon nie krediet byvoeg nie',
    failedToAddNote: 'Kon nie krediet byvoeg nie',
    creditNoteUpdatedSuccessfully: 'Krediet suksesvol opgedateer',
    noteUpdatedSuccessfully: 'Krediet suksesvol opgedateer',
    failedToUpdateCreditNote: 'Kon nie krediet opdateer nie',
    failedToUpdateNote: 'Kon nie krediet opdateer nie',
    deleteCreditNoteTitle: 'Verwyder Krediet',
    deleteNoteTitle: 'Verwyder Krediet',
    deleteCreditNoteConfirmMessage: 'Is jy seker jy wil hierdie krediet verwyder? Hierdie aksie kan nie ongedaan gemaak word nie.',
    deleteNoteConfirmMessage: 'Is jy seker jy wil hierdie krediet verwyder? Hierdie aksie kan nie ongedaan gemaak word nie.',
    creditNoteDeletedSuccessfully: 'Krediet suksesvol verwyder',
    noteDeletedSuccessfully: 'Krediet suksesvol verwyder',
    failedToDeleteCreditNote: 'Kon nie krediet verwyder nie',
    failedToDeleteNote: 'Kon nie krediet verwyder nie',
    voidCreditNote: 'Maak Kredietnota Ongeldig',
    voidCreditNoteConfirmMessage: 'Is jy seker jy wil hierdie kredietnota ongeldig maak? Hierdie aksie kan nie ongedaan gemaak word nie.',
    creditNoteVoidedSuccessfully: 'Krediet suksesvol ongeldig gemaak',
    failedToVoidCreditNote: 'Kon nie krediet ongeldig maak nie',
    action: 'Aksie',
    changedAtLabel: 'Verander Op',
    changedBy: 'Verander Deur',
    activate: 'Aktiveer',
    deactivate: 'Deaktiveer',
    confirm: 'Bevestig',
    phoneNumber: 'Telefoonnommer',
    address: 'Adres',
    keywords: 'Sleutelwoorde',
    autoMapKeywordsLabel: 'Outo-kartering Sleutelwoorde (komma-geskei)',
    autoMapKeywordsPlaceholder: 'bv. jan smit, huur, fooie',
    autoMapKeywordsHelper: 'Hierdie sleutelwoorde help met outomatiese kartering van staattransaksies.',
    farmWorkTypeUpdatedSuccessfully: 'Plaaswerktipe suksesvol opgedateer',
    farmWorkTypeCreatedSuccessfully: 'Plaaswerktipe suksesvol geskep',
    farmWorkTypesCreatedSuccessfully: 'Uitgawetipes suksesvol gestoor',
    toBeAdded: 'Moet bygevoeg word',
    noExpenseTypesStaged: 'Geen uitgawetipes bygevoeg nie. Voer \'n naam in hierbo en klik Voeg By.',
    addRow: 'Voeg Ry By',
    addAnotherExpenseType: 'Voeg Nog By',
    removeExpenseType: 'Verwyder',
    failedToSaveFarmWorkType: 'Kon nie plaaswerktipe stoor nie',
    farmWorkTypeDeletedSuccessfully: 'Plaaswerktipe suksesvol verwyder',
    failedToDeleteFarmWorkType: 'Kon nie plaaswerktipe verwyder nie',
    expenseTypeNameRequired: 'Uitgawetipe naam is verpligtend',
    expenseCostMustBeGreaterThanZero: 'Uitgawekoste moet groter as nul wees',
    failedValidateAvailableTypesMonth: 'Kon nie beskikbare plaaswerktipes vir maand valideer nie',
    pleaseSelectFarmWorkType: 'Kies asseblief ’n plaaswerktipe',
    farmWorkUpdatedSuccessfully: 'Plaaswerk suksesvol opgedateer',
    noSelectedMonthsHaveAvailableTypes: 'Geen geselekteerde maande het beskikbare plaaswerktipes nie.',
    failedToSaveFarmWork: 'Kon nie plaaswerk stoor nie',
    farmWorkDeletedSuccessfully: 'Plaaswerk suksesvol verwyder',
    failedToDeleteFarmWork: 'Kon nie plaaswerk verwyder nie',
    farmWorkAlreadyFullyCovered: 'Hierdie plaaswerkitem is reeds ten volle gedek.',
    contributionAmountMustBeGreaterThanZero: 'Bydraebedrag moet groter as nul wees',
    amountCannotBeNegative: 'Bedrag kan nie negatief wees nie',
    pleaseSelectAccountHolder: 'Kies asseblief ’n rekeninghouer',
    pleaseSelectAccountHolderForContribution: 'Kies asseblief ’n rekeninghouer vir die bydraer.',
    contributionCannotExceedRemainingCost: 'Bydrae kan nie die oorblywende koste oorskry nie:',
    contributionCannotExceedOutstandingCost: 'Bydrae kan nie die uitstaande koste oorskry nie:',
    contributionCannotExceedAllowableAmount: 'Bydrae kan nie die toegelate bedrag oorskry nie:',
    contributorsCannotExceedExpenseCost: 'Totale bydraerbedrae kan nie die uitgawekoste oorskry nie.',
    contributionAddedSuccessfully: 'Bydrae suksesvol bygevoeg',
    failedToAddContribution: 'Kon nie bydrae byvoeg nie',
    contributionRemovedSuccessfully: 'Bydrae suksesvol verwyder',
    failedToRemoveContribution: 'Kon nie bydrae verwyder nie',
    contributionUpdatedSuccessfully: 'Bydrae suksesvol opgedateer',
    failedToUpdateContribution: 'Kon nie bydrae opdateer nie',
    optional: '(Opsioneel)',
    selectAccountHolder: 'Kies rekeninghouer',
    selectedContributors: 'Geselekteerde bydraers:',
    totalContributionsCost: 'Totale bydraes',
    remaining: 'Oorblywend',
    accountHolderAlreadyAdded: 'Hierdie rekeninghouer is reeds bygevoeg',
    expenseTypeAlreadyAdded: 'Hierdie uitgawetipe is reeds bygevoeg',
    contributorAddedToList: 'Bydraer bygevoeg tot lys',
    expenseTypeAddedToList: 'Uitgawetipe bygevoeg tot lys',
    linkedToContributors: 'Gekoppel aan bydraers',
    cannotDeleteExpenseTypeWithContributors: 'Hierdie uitgawetipe kan nie verwyder word nie omdat dit aan bydraerinskrywings gekoppel is.',
    expenseTypeDeleteBlockedTooltip: 'Kan nie verwyder nie: gekoppel aan bydraerinskrywings',
    contributor: 'bydraer',
    contributorPlural: 'bydraers',
    addedSuccessfully: 'suksesvol bygevoeg',
    failedToAddContributors: 'Kon nie bydraers byvoeg nie',
    enterDescription: 'Voer omskrywing in (opsioneel)',
    overpaid: 'oor betaal',
    profileTitle: 'Gebruikersprofiel',
    profilePreferences: 'Lokaliseringsvoorkeure',
    dashboardSettingsTitle: 'Admin Stelselinstellings',
    dashboardSettingsSubtitle: 'Bestuur stelselverstekwaardes, transaksie-outokartering en trustrekening-onderhoud.',
    couldNotLoadDashboardSettingsData: 'Kon nie dashboard-instellingsdata laai nie',
    dashboardDefaultsSavedSuccessfully: 'Dashboard-verstekwaardes suksesvol gestoor',
    displayDefaults: 'Vertoon Verstekwaardes',
    defaultRowsPerPage: 'Verstek rye per bladsy',
    showSystemFeesChargesByDefault: 'Wys stelselfooie/heffings by verstek in transaksietabel',
    saveDisplayDefaults: 'Stoor vertoon verstekwaardes',
    transactionAutomapperKeywords: 'Transaksie Outokartering Sleutelwoorde',
    transactionAutomapperKeywordsHelper: 'Stel sleutelwoordpassing op vir stelselfooi/heffingskategorieë wat deur die uitpak-outokarteerder gebruik word.',
    savingsKeywordsLimitedCategories: 'Spaarkartering ondersteun net twee kategoriee: Stelselfooie en Algemeen.',
    keywordChanges: 'Sleutelwoordveranderings',
    categoryPending: 'kategorie hangend',
    categoriesPending: 'kategorieë hangend',
    saveAllKeywordChanges: 'Stoor alle sleutelwoordveranderings',
    savingAllKeywordChanges: 'Stoor alles...',
    noKeywordChangesToSave: 'Geen sleutelwoordveranderings om te stoor nie',
    savedAllKeywordChanges: 'Alle sleutelwoordveranderings is gestoor',
    couldNotSaveAllKeywordChanges: 'Kon nie alle sleutelwoordveranderings stoor nie',
    unsavedChanges: 'Ongebergde veranderings',
    allChangesSaved: 'Alle veranderings gestoor',
    unsaved: 'Ongeberg',
    addOrRemoveKeywordsHint: 'Voeg sleutelwoorde hieronder by of verwyder, en stoor dan om veranderinge toe te pas.',
    noKeywordsConfigured: 'Geen sleutelwoorde opgestel nie',
    enterKeywordsCommaSeparated: 'Voer sleutelwoord(e) in, komma-geskei',
    add: 'Voeg by',
    cancelAddFarmWorkConfirmMessage: 'Is jy seker jy wil kanselleer? Jou ongebergde uitgawebesonderhede sal verlore gaan.',
    cancelAddFarmWorkTypeConfirmMessage: 'Is jy seker jy wil kanselleer? Jou ongebergde uitgawetipe-veranderings sal verlore gaan.',
    cancelAddEquipmentHiringConfirmMessage: 'Is jy seker jy wil kanselleer? Jou ongebergde toerustinghuur-besonderhede sal verlore gaan.',
    activeKeywords: 'Aktiewe sleutelwoorde',
    none: 'Geen',
    total: 'Totaal',
    incoming: 'Inkomend',
    outgoing: 'Uitgaand',
    net: 'Netto',
    category: 'Kategorie',
    accountNumberLabel: 'Rekening #',
    statementAccountTypeLabel: 'Tipe rekening:',
    statementSourceCurrentPreview: 'Staatbron: huidige voorskou (nie gestoor nie)',
    statementSourceLastImport: 'Staatbron: laaste invoer',
    noStatementAccountInfoTitle: 'Nog geen staatrekeningbesonderhede nie',
    noStatementAccountInfoMessage: 'Voer ’n trustrekeningstaat in om rekeningnommer, tipe en tydperk te vul.',
    importStatementToPopulateSummary: 'Voer ’n staat in om saldo’s en rekeningopsomming te vul.',
    availableBalanceLabel: 'Beskikbaar:',
    unclearedChequesLabel: 'Ongeklaarde tjeks:',
    lastImportedLabel: 'Laas ingevoer:',
    extractingTransactions: 'Pak transaksies uit…',
    statementExtractionInProgressHint: 'Lees en koppel transaksies vanaf jou staat. Dit neem gewoonlik net ’n paar sekondes.',
    savingTransactions: 'Stoor transaksies…',
    statementSavingInProgressHint: 'Stoor hersiene transaksies en werk jou trustrekening-opsomming op.',
    statementProcessingVisibilityHint: 'Hou asseblief hierdie oortjie oop totdat verwerking klaar is.',
    reviewModalSavingVisibilityHint: 'Wag asseblief terwyl karterings en saldo\u2019s gefinaliseer word.',
    closeImportConfirmTitle: 'Voer in kanselleer?',
    closeImportConfirmMessage: 'Jy het \u2019n invoer aan die gang. As jy nou sluit, sal alle uitgepakte transaksies verwyder word. Is jy seker jy wil sluit?',
    closeImportConfirmButton: 'Ja, gooi invoer weg',
    importStatementDropzone: 'Voer Staat In (slegs PDF) — klik of sleep en los',
    createAccountHolderBeforeStatementImport: 'Geen rekeninghouers gevind nie. Skep eers ’n rekeninghouer voordat jy ’n staat invoer.',
    configureAutomapperKeywords: 'Stel outokartering sleutelwoorde op',
    showSystemFeesChargesInTransactions: 'Wys stelselfooie/heffings in transaksies',
    noTrustAccountTransactionsForFilters: 'Geen trustrekeningtransaksies vir die geselekteerde filters gevind nie',
    noAccountHolderSummaryForFilters: 'Geen rekeninghouer-opsommingsdata vir die geselekteerde filters beskikbaar nie',
    noSystemFeeChargeCategoriesForFilters: 'Geen stelselfooi- of heffingskategorieë vir die geselekteerde filters gevind nie',
    unknown: 'Onbekend',
    trustAccountTransactions: 'Trust Rekening Transaksies',
    selectMonth: 'Kies maand',
    showing: 'Wys',
    of: 'van',
    accountHolderSummary: 'Rekeninghouer Opsomming',
    accountHolderSummarySubtitle: 'Inkomend en uitgaand slegs, uitsluitend rekeningfooitransaksies',
    recentTransactions: 'Onlangse Transaksies',
    transactionSummary: 'Transaksie Opsomming',
    accountOverviewSection: 'Rekening Oorsig',
    expenseSummarySection: 'Uitgawe Opsomming',
    manageArea: 'Bestuur Area',
    manageAreaSubtitle: 'Kies wat jy hieronder wil bestuur',
    expensesTabSubtitle: 'Volg uitgawes, bydraes en dekking',
    equipmentHiring: 'Toerustinghuur',
    equipmentHiringTabSubtitle: 'Bestuur huurkoste, inkomste en ontvangers',
    farmWorkAndEquipmentTitle: 'Plaaswerk en Toerusting',
    expenseEquipmentSectionSwitcherAria: 'Uitgawe- en toerustingafdeling-skakelaar',
    expenseEquipmentViewsAria: 'Uitgawe- en toerustingaansigte',
    activeSection: 'Aktiewe Afdeling',
    activeSectionFarmWorkSubtitle: 'Uitgawe-inskrywings en bydraeropsporing',
    activeSectionEquipmentSubtitle: 'Huurkoste, inkomsteverdeling en toewysings',
    addEquipmentHiring: 'Voeg Toerustinghuur By',
    editEquipmentHiring: 'Wysig Toerustinghuur',
    equipmentHiringItemsTitle: 'Toerustinghuur-items',
    noEquipmentHiringEntriesForSelectedMonth: 'Geen toerustinghuur-inskrywings vir die gekose maand gevind nie',
    equipmentLabel: 'Toerusting',
    equipmentPlaceholder: 'bv. Trekker / Stroper / Sleepwa',
    selectEquipmentDatePlaceholder: 'Kies toerustingdatum',
    equipmentRowAriaPrefix: 'Toerustingry',
    incomeRecipients: 'Inkomste-ontvangers',
    incomeRecipientsCredits: 'Inkomste-ontvangers (krediete)',
    noContributorsYet: 'Nog geen bydraers nie',
    noIncomeRecipientsYet: 'Nog geen inkomste-ontvangers nie',
    incomeRecipient: 'Inkomste-ontvanger',
    addIncomeRecipient: 'Voeg Inkomste-ontvanger By',
    incomeShare: 'Inkomstedeel',
    clickToRemoveLabelPrefix: 'Klik om te verwyder',
    equipmentRequired: 'Toerusting is verpligtend.',
    dateRequired: 'Datum is verpligtend.',
    pleaseSelectValidDate: 'Kies asseblief ’n geldige datum.',
    equipmentHiringUpdatedSuccessfully: 'Toerustinghuur suksesvol opgedateer.',
    equipmentHiringCreatedSuccessfully: 'Toerustinghuur suksesvol geskep.',
    failedToSaveEquipmentHiring: 'Kon nie toerustinghuur stoor nie.',
    deleteEquipmentHiringTitle: 'Verwyder Toerustinghuur',
    deleteEquipmentHiringConfirmMessage: 'Is jy seker jy wil hierdie toerustinghuur-inskrywing verwyder?',
    equipmentHiringDeletedSuccessfully: 'Toerustinghuur suksesvol verwyder.',
    failedToDeleteEquipmentHiring: 'Kon nie toerustinghuur verwyder nie.',
    equipmentContributorUpdatedSuccessfully: 'Toerustingbydraer suksesvol opgedateer.',
    equipmentContributorAddedSuccessfully: 'Toerustingbydraer suksesvol bygevoeg.',
    failedToUpdateEquipmentContributor: 'Kon nie toerustingbydraer opdateer nie.',
    failedToAddEquipmentContributor: 'Kon nie toerustingbydraer byvoeg nie.',
    removeEquipmentContributorTitle: 'Verwyder Toerustingbydraer',
    removeEquipmentContributorConfirmPrefix: 'Is jy seker jy wil',
    removeEquipmentContributorConfirmSuffix: 'uit hierdie toerustinghuur-inskrywing verwyder?',
    equipmentContributorRemovedSuccessfully: 'Toerustingbydraer suksesvol verwyder.',
    failedToRemoveEquipmentContributor: 'Kon nie toerustingbydraer verwyder nie.',
    incomeRecipientUpdatedSuccessfully: 'Inkomste-ontvanger suksesvol opgedateer.',
    incomeRecipientAddedSuccessfully: 'Inkomste-ontvanger suksesvol bygevoeg.',
    failedToUpdateIncomeRecipient: 'Kon nie inkomste-ontvanger opdateer nie.',
    failedToAddIncomeRecipient: 'Kon nie inkomste-ontvanger byvoeg nie.',
    removeIncomeRecipientTitle: 'Verwyder Inkomste-ontvanger',
    removeIncomeRecipientConfirmPrefix: 'Is jy seker jy wil',
    removeIncomeRecipientConfirmSuffix: 'uit inkomste-ontvangers verwyder?',
    incomeRecipientRemovedSuccessfully: 'Inkomste-ontvanger suksesvol verwyder.',
    failedToRemoveIncomeRecipient: 'Kon nie inkomste-ontvanger verwyder nie.',
    totalEquipmentCost: 'Totale Toerustingkoste',
    totalContributorsLabel: 'Totale Bydraers',
    incomeGenerated: 'Inkomste Gegenereer',
    hoursHired: 'Ure Gehuur',
    savingsAccountStats: 'Spaarrekening Statistiek',
    savingsInterestVsSystemFees: 'Spaar rente teenoor stelselfooie/heffings',
    savingsInterestVsSystemFeesLastThreeMonths: 'Spaar Rente vs Stelselfooie/Heffings (Laaste 3 Maande)',
    interestGain: 'Rentewins',
    interestIncome: 'Rente-inkomste',
    netAfterSystemCharges: 'Netto na stelselfooie',
    totalBalance: 'Totale Balans',
    totalIncome: 'Totale Inkomste',
    totalExpenses: 'Totale Uitgawes',
    netMovement: 'Netto Beweging',
    systemFeesChargesOverview: 'Stelselfooie & Heffings Oorsig',
    systemFeeChargeLabel: 'Stelselfooi/heffing',
    sendingStatementEmail: 'Stuur staat-e-pos',
    noPaymentSummariesFoundForMonth: 'Geen betalingsopsommings gevind vir',
    reviewExtractedRowsPrefix: 'Hersien',
    reviewExtractedRowsSuffix: 'uitgepakte rye.',
    transactionsReadyToSave: 'transaksie(s) is gereed om te stoor',
    autoMappedByExtraction: 'outomaties gekarteer tydens uitpakking',
    alreadyExistsMayBeSkippedOrUpdated: 'bestaan reeds en kan oorgeslaan word tensy opgedateer',
    couldNotExtractTransactionsFromFile: 'Kon nie transaksies uit hierdie lêer uitpak nie. Bevestig die lêer is ’n geldige trustrekening CSV of PDF-staat en probeer weer.',
    markedTransactionIgnoredOnSave: 'Transaksie gemerk om by stoor te ignoreer.',
    updatedExistingMappingFrom: 'Bestaande kartering opgedateer van',
    previousAccountHolder: 'vorige rekeninghouer',
    to: 'na',
    removedExistingMappingForTransaction: 'Bestaande kartering vir hierdie transaksie verwyder.',
    mappedTransactionTo: 'Transaksie gekarteer na',
    transactionsStillNeedAccountHolderMapping: 'transaksie(s) benodig steeds rekeninghouer-kartering',
    savingReviewedTransactions: 'Stoor hersiene transaksies. Dit kan ’n paar sekondes neem...',
    reviewExtractedTransactions: 'Hersien uitgepakte transaksies',
    verifyTransactionMappingsBeforeSaving: 'Verifieer elke transaksie en bevestig die korrekte rekeninghouer-kartering voordat jy stoor.',
    noPreviewDataAvailable: 'Geen voorskoudata beskikbaar nie.',
    extractionOverviewTitle: 'Uitpak-oorsig',
    extractionOverviewSubtitle: 'Bevestig opskrifbesonderhede en hersien gereedheid voor stoor.',
    statementPeriod: 'Staattydperk',
    statementAccountNumberLabel: 'Rekeningnommer',
    statementAccountNumberAutoDetectedBadge: 'Outomaties opgespoor',
    statementAccountNumberAutoDetectedTooltip: 'Outomaties uit die ingevoerde lêer opgespoor. Verifieer dit asseblief voor stoor.',
    enterStatementAccountNumber: 'Voer rekeningnommer vanaf staat in',
    statementAccountNumberHelper: 'As rekeningnommer nie outomaties uitgehaal word nie, vul dit hier in voor stoor.',
    statementAccountNumberReadOnlyHelper: 'Rekeningnommer is outomaties vanaf die staat-opskrif ingevul.',
    statementAccountNumberEditAction: 'Wysig',
    statementAccountNumberLockAction: 'Sluit',
    readyToSave: 'Gereed om te stoor',
    needsMapping: 'Benodig kartering',
    willBeSkipped: 'Sal oorgeslaan word',
    ignored: 'Geïgnoreer',
    invalidRows: 'Ongeldige rye',
    totalRows: 'Totale rye',
    extractionReviewInfoMessage: 'Opskrif- en nie-transaksie-rye word uit voorskou uitgesluit. Bestaande ooreenstemmende transaksies word outomaties opgespoor en sal oorgeslaan word tensy jy kartering verander om dit op te dateer. Gebruik "Ignoreer hierdie transaksie" vir rye wat nie ingevoer moet word nie.',
    selectAccountHolderOption: '-- Kies rekeninghouer --',
    selectCategoryOption: '-- Kies kategorie --',
    savingsSystemFeesOptionLabel: 'Stelselfooie - bank/diensheffings',
    savingsGeneralOptionLabel: 'Algemeen - rentekrediete',
    ignoreThisTransaction: 'Ignoreer hierdie transaksie',
    transactionWillBeIgnoredDuringImport: 'Hierdie transaksie sal tydens invoer geïgnoreer word.',
    autoMappedToSystemFeeCategory: 'Outomaties gekarteer na stelselfooi/heffingskategorie',
    willAutoCategorizeOnSaveAs: 'Sal outomaties by stoor gekategoriseer word as',
    matchingExistingTransactionFoundMessage: 'Ooreenstemmende bestaande transaksie gevind; stoor sal dit oorslaan tensy karteringsveranderinge ’n opdatering vereis.',
    validationDetails: 'Valideringsbesonderhede',
    invalidRow: 'Ongeldige ry',
    missingRequiredValues: 'Ontbrekende vereiste waardes',
    saveStatementTransactions: 'Stoor staattransaksies',

    chatBotLabel: 'Hulp',
    chatBotTitle: 'Hulp & Ondersteuning',
    chatBotWelcome: 'Hallo! Hoe kan ek jou help om Savannah Rekeninge te gebruik?',
    chatBotTopicsPrompt: "Kies 'n onderwerp om te begin:",
    chatBotBack: '← Terug na onderwerpe',
    chatBotTopicDashboard: 'Kontroleskerm',
    chatBotTopicDashboardContent: "Die Kontroleskerm wys jou trustrekening se saldo en aktiwiteit. Gebruik die rekeningwisselaar bo-aan om tussen Lopende en Spaarrekeninge te wissel. Die Maandelikse Vergelyking-grafiek wys krediete en debiete vir die laaste vier maande. Die Onlangse Transaksies-paneel lys die nuutste inskrywings vir die geselekteerde rekening. Jy kan ook staattransaksies vanaf hierdie skerm invoer deur 'n lêer op te laai, die uitgeleesde rye te hersien en die gekarteerde resultate te stoor.",
    chatBotTopicAccountHolders: 'Rekeninghouers',
    chatBotTopicAccountHoldersContent: 'Rekeninghouers is individue of entiteite gekoppel aan jou trustrekening. Jy kan houers byvoeg, wysig, aktiveer of deaktiveer. Elke houer ondersteun verskeie adresreëls met afsonderlike maandelikse fooie. Gebruik Kredietnotas om uitstaande bedrae te verreken, en stuur pasgemaakte e-posse aan gekose houers direk vanuit die lys.',
    chatBotTopicExpenses: 'Uitgawes',
    chatBotTopicExpensesContent: "Die Uitgawes-bladsy bestuur plaaswerkkostes en toerustinghuur. Begin deur Uitgawetipes by te voeg, skep dan maandelikse uitgawe-items. Wys bydraers toe om kostes onder rekeninghouers te verdeel. Die Toerustinghuur-oortjie volg huurkoste, gewerkte ure en inkomste gegenereer per rekeninghouer.",
    chatBotTopicPayments: 'Betalings',
    chatBotTopicPaymentsContent: 'Die Betalings-bladsy (Rekening Oorsig) wys alle trustrekeningtransaksies. Voeg enkel- of bondel-betalings by, filter op rekeninghouer en datumreeks, en wysig of verwyder bestaande inskrywings. Die Transaksie Opsomming-paneel totaliseer krediete en debiete vir die geselekteerde filters.',
    chatBotTopicStatements: 'State',
    chatBotTopicStatementsContent: "State word gestuur vanuit die Betalings-bladsy. Kies 'n datumreeks en trustrekening, kies een of meer bydraers, en klik dan Stuur E-pos om hul staat te e-pos. Jy kan ook 'n staat direk vanuit die blaaier druk. Elke staat sluit 'n saldo-oorsig, uitgawebydraes en bankbetalingsbesonderhede in.",
    chatBotTopicSettings: 'Instellings',
    chatBotTopicSettingsContent: "Instellings is slegs vir Administrateurs toeganklik. Admin Stelselinstellings laat jou toe om vertoon-verstekwaardes en transaksie-outokartering-sleutelwoorde op te stel. Gebruikersbestuur laat jou toe om stelselgebruikers te skep, wysig of verwyder en hul rolle in te stel. Trustrekening-opstelling word ook vanuit hierdie area bestuur.",
    chatBotInputPlaceholder: 'Vra hoe om ’n bladsy of funksie te gebruik...',
    chatBotSend: 'Stuur',
    chatBotClear: 'Maak gesprek skoon',
    chatBotUnknown: 'Ek kon nog nie ’n direkte antwoord daarvoor vind nie. Probeer vra oor Kontroleskerm, Rekeninghouers, Uitgawes, Betalings, State, of Instellings.',
    chatBotSuggestions: 'Probeer vrae soos:',
    chatBotSuggestionOne: 'Hoe stuur ek state?',
    chatBotSuggestionTwo: 'Hoe voeg ek ’n rekeninghouer by?',
    chatBotSuggestionThree: 'Hoe neem ek uitgawes op?',
    chatBotSuggestionDashboard: 'Hoe werk die Kontroleskerm?',
    chatBotSuggestionPayments: 'Hoe sien ek transaksies?',
    chatBotSuggestionSettings: 'Hoe stel ek instellings op?',
    chatBotSuggestionTrustAccount: 'Hoe werk trustrekeninge?',
    chatBotSuggestionCreditNote: 'Hoe voeg ek ’n kredietnota by?',
    chatBotSuggestionUsers: 'Hoe bestuur ek gebruikers?',
    chatBotMinimizeSuggestions: 'Minimeer',
    chatBotShowSuggestions: 'Wys voorstelle',
    chatBotDismissSuggestions: 'Maak toe',
    chatBotNavigationLabel: 'Navigasie',
    chatBotOpenPage: 'Maak bladsy oop',
    chatBotStepsLabel: 'Hoe-om stappe',
    chatBotScreenshotLabel: 'Skermgids',
    chatBotDashboardStep1: 'Maak Dashboard oop en kies die trustrekening waarmee jy wil werk met die rekeningwisselaar.',
    chatBotDashboardStep2: 'Gebruik die maandkieser, houerfilter en stelseltransaksie-skakelaar om die vertoning te verfyn.',
    chatBotDashboardStep3: 'Laai jou bankstaatlêer op in die staatinvoerder-paneel (PDF of teks-uitvoer).',
    chatBotDashboardStep4: 'Hersien die uitgeleesde rye in die voorskouvenster, koppel elke ry aan ’n rekeninghouer, herstel rekeningnommer indien nodig, en merk irrelevante rye as geïgnoreer.',
    chatBotDashboardStep5: 'Klik Stoor staattransaksies en bevestig dat die ingevoerde rekords in Onlangse Transaksies en maandelikse totale verskyn.',
    chatBotAccountHoldersStep1: 'Maak Rekeninghouers oop en klik Voeg Nuwe Rekeninghouer By om ’n nuwe profiel te skep.',
    chatBotAccountHoldersStep2: 'Vul houerbesonderhede in, voeg adreslyne met maandelikse fooie by, en stel sleutelwoorde vir passing op.',
    chatBotAccountHoldersStep3: 'Stoor die houer en gebruik Wysig, Aktiveer of Deaktiveer aksies in die houertabel wanneer opdaterings nodig is.',
    chatBotAccountHoldersStep4: 'Maak Notas vir ’n houer oop om kredietnotas by te voeg, te wysig of te verwyder vir uitstaande bedrae.',
    chatBotAccountHoldersStep5: 'Gebruik Stuur Pasgemaakte E-pos om ontvangers te kies, die boodskap op te stel, en te stuur of as konsep te hou.',
    chatBotExpensesStep1: 'Maak Uitgawes oop, kies die maand, en wissel tussen Plaaswerk en Toerustinghuur afdelings.',
    chatBotExpensesStep2: 'Gebruik Bestuur Uitgawetipes om plaaswerkkategorieë by te voeg, te wysig of te verwyder voordat kostes vasgelê word.',
    chatBotExpensesStep3: 'Skep ’n plaaswerkuitgawe, stel bedrag en herhalende maande in, en stoor dan die inskrywing.',
    chatBotExpensesStep4: 'Gebruik Voeg Bydraer By om houerbydraes toe te wys, en wysig of verwyder bydraes soos nodig.',
    chatBotExpensesStep5: 'In Toerustinghuur, lê huurkostes, ure, inkomste vas en ken beide bydraers en inkomste-ontvangers toe.',
    chatBotPaymentsStep1: 'Maak Betalings oop en kies die trustrekening-konteks wat jy wil hersien.',
    chatBotPaymentsStep2: 'Pas houer- en maandfilters toe, en gebruik Herstel Filters om na die verstek-aansig terug te keer.',
    chatBotPaymentsStep3: 'Gebruik Voeg Nuwe Betaling By vir ’n enkele transaksie met houer, bedrag, datum en beskrywing.',
    chatBotPaymentsStep4: 'Gebruik Voeg Nuwe Betalings By om ’n konseplys vir bondelvaslegging te bou, en stoor dan die ry-inskrywings saam.',
    chatBotPaymentsStep5: 'Hersien opsommingskaarte en transaksietabeltotale, en maak dan State Sentrum oop vir staat- en e-posaksies.',
    chatBotStatementsStep1: 'Maak State Sentrum oop, kies maand- en houerfilters, en laai die tydperk wat jy nodig het.',
    chatBotStatementsStep2: 'Vou houerrye oop om plaaswerk-, fooi- en bydrae-opsommingstabelle te hersien voor stuur.',
    chatBotStatementsStep3: 'Gebruik Druk op ’n enkele houer of geselekteerde houers om blaaier-drukgereed state te genereer.',
    chatBotStatementsStep4: 'Gebruik Stuur E-pos vir een houer of bondel-e-pos vir geselekteerde houers nadat totale nagegaan is.',
    chatBotStatementsStep5: 'Kontroleer KPI-kaarte en bydraerstatus-kentekens om uitstaande, gedeeltelike of ten volle betaalde statusse te bevestig.',
    chatBotSettingsStep1: 'Maak Instellings > Admin Stelselinstellings oop om trustrekeningbesonderhede te skep en te wysig.',
    chatBotSettingsStep2: 'Stel verstek bladsygrootte en stelselfooivertoning in, en stoor dan vertoonverstekwaardes.',
    chatBotSettingsStep3: 'Wissel sleutelwoord-rekeningtipe en bestuur outokartering-kategorieë deur sleutelwoorde by te voeg, te verwyder of terug te stel.',
    chatBotSettingsStep4: 'Stoor sleutelwoordveranderings en bevestig dat kategorie-statuskentekens van hangende na gestoor verander.',
    chatBotSettingsStep5: 'Maak Instellings > Gebruikersbestuur oop om gebruikers te skep, te wysig en te verwyder en hul toegangsrolle te bestuur.',
  },
};

const LocalizationContext = createContext<LocalizationContextType | undefined>(undefined);

export const LocalizationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const userId = user?.id;
  const [language, setLanguage] = useState<AppLanguage>('en');
  const [currency, setCurrency] = useState<AppCurrency>('ZAR');
  const [isHydratedFromServer, setIsHydratedFromServer] = useState(false);

  useEffect(() => {
    if (!userId) {
      setLanguage('en');
      setCurrency('ZAR');
      setIsHydratedFromServer(false);
      return;
    }

    let isDisposed = false;

    const loadLocalizationPreference = async () => {
      try {
        const settings = await dashboardSettingsService.get();
        if (isDisposed) {
          return;
        }

        setLanguage(settings.localizationLanguage === 'af' ? 'af' : 'en');
        setCurrency(settings.localizationCurrency === 'USD' ? 'USD' : 'ZAR');
      } catch {
        if (isDisposed) {
          return;
        }

        setLanguage('en');
        setCurrency('ZAR');
      } finally {
        if (!isDisposed) {
          setIsHydratedFromServer(true);
        }
      }
    };

    setIsHydratedFromServer(false);
    loadLocalizationPreference();

    return () => {
      isDisposed = true;
    };
  }, [userId]);

  useEffect(() => {
    if (!userId || !isHydratedFromServer) {
      return;
    }

    dashboardSettingsService.update({
      localizationLanguage: language,
      localizationCurrency: currency,
    }).catch(() => {
    });
  }, [userId, isHydratedFromServer, language, currency]);

  const locale = languageLocales[language];

  const value: LocalizationContextType = {
    language,
    currency,
    locale,
    setLanguage,
    setCurrency,
    formatCurrency: (amount: number) => {
      const formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      });

      if (currency !== 'ZAR') {
        return formatter.format(amount);
      }

      return formatter
        .formatToParts(amount)
        .map((part) => (part.type === 'currency' ? 'R' : part.value))
        .join('');
    },
    formatDate: (value: string | Date, options?: Intl.DateTimeFormatOptions) => {
      const date = value instanceof Date ? value : new Date(value);
      return date.toLocaleDateString(locale, options);
    },
    formatMonthYear: (value: string | Date) => {
      const date = value instanceof Date ? value : new Date(value);
      return date.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    },
    t: (key: string) => translations[language][key] ?? translations.en[key] ?? key,
  };

  return <LocalizationContext.Provider value={value}>{children}</LocalizationContext.Provider>;
};

export const useLocalization = () => {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error('useLocalization must be used within a LocalizationProvider');
  }

  return context;
};
